import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
// Removed useNavigate import
import comms from '../../comms';
import { initializeSocket, formatPhone } from './socketHelper';
import WaypointModal from './TripModal';
import Itinerary from './Itinerary';
import GeolocationHandler from './GeolocationHandler';
import {
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
  jobLegToLocationArray,
  waypointAddressFromLeg,
  PASSENGER_DETERMINED_DROP,
} from './locationHelper';
import GpsWarningTag from './GpsWarningTag';
import WaypointMap from './WaypointMap';
import Messages from './Messages';
import useSoftMeter from '../../../hooks/useSoftMeter';
import useGeofenceArrival from '../../../hooks/useGeofenceArrival';
import { parseMeterConfig } from '../../../services/softMeter';
import { extractJobPricing } from '../../../utils/extractJobPricing';
import { ON_DEMAND_OFFER_BUFFER_SECONDS } from '../../../utils/onDemandOffer';
import { resolveTripPaymentStatus, tripIdKey, mergePaymentOverride, TRIP_PAYMENT_STATE } from '../../../utils/tripPaymentStatus';
import { receiptPaymentMethod, editableChargeRows, buildCollectionItems } from '../../../utils/tripPaymentSummary';
import FareConfirmationModal from './FareConfirmationModal';
import ItineraryFinalFareModal from '../Itinerary/ItineraryFinalFareModal';
import AddOnChargesSheet from './AddOnChargesSheet';
import PaymentCollectionSheet from './PaymentCollectionSheet';
import usePassengerOtpDialogue from './usePassengerOtpDialogue';
import { configureOsrm } from '../../../services/osrmMatch';
import { resolveVehicleUnitId } from '../../../utils/resolveVehicleUnitId';
import { getDirections } from '../../../services/directionsService';

/**
 * How long to leave a Busy/Clear button in its "sending" state before letting the
 * driver press it again. AutoDispatch acks with BUSY|Ok / CLEAR|Ok and answers a
 * refusal with START, so this only matters when nothing comes back at all.
 */
export const VEHICLE_STATUS_ACK_TIMEOUT_MS = 8000;

/** Normalize trip/job id so 55644 and "55644" share one pricing bucket. */
function tripKey(id) {
  if (id === null || id === undefined || id === '') return '';
  return String(id).trim();
}

/** Normalize waypoint type for sort / filters (server may use different casing). */
function waypointTypeKey(t) {
  return String(t || '').trim().toLowerCase();
}

/**
 * Store a JOB/START payment block under the trip's jobNumber bucket. A settlement that
 * arrived earlier under the bare Trip.Id (before this JOB was ingested) is folded in so
 * the live Paid state is never overwritten by a payload captured before it.
 */
function mergeIngestedPayment(prev, jnKey, jobID, payment) {
  const gk = tripIdKey(jobID);
  const live = (gk && prev[gk]) || null;
  const next = { ...prev };
  next[jnKey] = live && live.paid ? mergePaymentOverride(payment, live) : payment;
  if (gk && gk !== jnKey && next[gk]) delete next[gk];
  return next;
}

const IDLE_FINALIZE = { tripNumber: null, status: 'idle', error: null, priceItems: null, total: null };
const EMPTY_COLLECTION = { tripId: null, url: null, error: null };
const EMPTY_RECEIPT = { loading: false, url: null, error: null, messageSid: null, sentTo: null };

/** Format a leg's eta (unix seconds) as a local time string. Flag trips and
 * some on-the-fly jobs don't carry an eta — new Date(NaN).toLocaleTimeString()
 * silently returns the literal string "Invalid Date", which was rendering
 * as-is in the UI. Returns '' when eta is missing/invalid instead. */
function formatEtaTime(etaSeconds) {
  const ms = Number(etaSeconds) * 1000;
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  // Hour+minute only — seconds add no value to a scheduled pickup/dropoff time
  // and made the string too wide to sit on one line on narrow phone screens.
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * The trip's SCHEDULED PICKUP time, from job.info.scheduled_time — the time half of the
 * server's trip.OriginScheduledDateTime ("HH:mm:ss", dispatch.js ~4160/4231). This is the
 * right source for a "Schedule Time" display: locations[0].eta is only
 * "OriginScheduledDateTime + 5 minutes" (an arrival estimate), and it arrives as NaN
 * whenever the server's moment() parse of that column fails — which is why the header
 * showed N/A on flag trips.
 */
function formatScheduledPickupTime(info) {
  const raw = info && info.scheduled_time;
  if (!raw) return '';
  const parts = String(raw).trim().split(':');
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Short beep for the GPS geofence auto-arrive confirmation — generated via
 * Web Audio so we don't ship/embed an audio asset for one chime. */
function playArrivalChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => ctx.close().catch(() => {});
  } catch {
    // best-effort — silence is an acceptable degradation
  }
}

/**
 * Sort stops for the driver: same trip → Pickup before Dropoff (bad ETAs often invert this);
 * different trips → by ETA.
 */
function sortWaypointsDrivingOrder(waypoints) {
  const typeRank = (t) => {
    const k = waypointTypeKey(t);
    if (k === 'pickup') return 0;
    if (k === 'break') return 1;
    if (k === 'dropoff') return 2;
    return 99;
  };
  return [...waypoints].sort((a, b) => {
    const ja = tripKey(a.jobNumber);
    const jb = tripKey(b.jobNumber);
    if (ja !== jb) {
      return (a.timeStamp || 0) - (b.timeStamp || 0);
    }
    const oa = typeRank(a.type);
    const ob = typeRank(b.type);
    if (oa !== ob) return oa - ob;
    return (a.timeStamp || 0) - (b.timeStamp || 0);
  });
}

/**
 * SoftMeter disabled for ACET specifically, for now — requested to be pulled while its
 * meter-rate configuration is sorted out on that fleet. Remove this once ACET is ready
 * for SoftMeter again; every other fleet is unaffected.
 */
function isSoftMeterDisabledForFleet() {
  try {
    const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings') || 'null');
    const fleetCode = fleetSettings?.unitId || localStorage.getItem('fleetCode') || '';
    return String(fleetCode).toUpperCase() === 'ACET';
  } catch {
    return false;
  }
}

/** True if raw meter JSON parses to something that can bill (not an empty stub). */
function hasUsableMeterRates(rawMeter) {
  if (isSoftMeterDisabledForFleet()) return false;
  const cfg = parseMeterConfig(rawMeter);
  if (!cfg) return false;
  return (
    cfg.flagRate > 0 ||
    cfg.distancePrice > 0 ||
    (cfg.distanceBlockMiles > 0 && cfg.distanceBlockRate > 0) ||
    cfg.timeUnitRate > 0 ||
    cfg.minimumFare > 0
  );
}

/** Statuses that mean "trip is in progress" — Flag Trip is hidden while any of these holds. */
const IN_PROGRESS_TRIP_STATUSES = new Set([
  'dispatched',
  'enroute',
  'arrivedorigin',
  'loaded',
  'arriveddestination',
]);

function tripIsInProgress(trip) {
  if (!trip) return false;
  const s = String(trip.status || '').trim().toLowerCase();
  return IN_PROGRESS_TRIP_STATUSES.has(s);
}

/** Keys fleet APIs may use for street / flag trip (camelCase, PascalCase, etc.). */
const ALLOW_FLAG_FLEET_KEYS = [
  'allowFlag',
  'AllowFlag',
  'allow_flag',
  'enableFlagTrip',
  'EnableFlagTrip',
  'flagPassenger',
  'FlagPassenger',
];

function firstDefinedFleetValue(settings, keys) {
  if (!settings || typeof settings !== 'object') return undefined;
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(settings, k)) continue;
    const v = settings[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function coerceFleetBoolean(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes' || s === 'y') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === 'n') return false;
  }
  return false;
}

/**
 * Whether to show Flag Trip in LiveDispatch. Older APIs omit allowFlag entirely — we default ON
 * so the control is visible unless the fleet explicitly disables it. Override with
 * REACT_APP_ALLOW_FLAG_TRIP=true|false.
 */
function fleetAllowsFlagTrip(settings) {
  const env = String(process.env.REACT_APP_ALLOW_FLAG_TRIP || '')
    .trim()
    .toLowerCase();
  if (env === 'false' || env === '0' || env === 'no') return false;
  if (env === 'true' || env === '1' || env === 'yes') return true;
  const raw = firstDefinedFleetValue(settings, ALLOW_FLAG_FLEET_KEYS);
  if (raw === undefined) return true;
  return coerceFleetBoolean(raw);
}

function LiveDispatch({
  navigateTo,
  userSettings,
  fleetSettings,
  socket,
  driverData,
  googleMapsLoaded = false,
}) {
  // Removed navigate
  const [jobDetails, setJobDetails] = useState([]);
  const jobDetailsRef = useRef([]);
  useEffect(() => {
    jobDetailsRef.current = jobDetails;
  }, [jobDetails]);

  const [driverDetails, setDriverDetails] = useState(null);
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const mapContainerRef = useRef(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [center, setCenter] = useState({ lat: 28.05, lng: -82.4483 });
  const [currentEta, setCurrentEta] = useState(null);
  /** { minutes, usesTraffic } from last successful directions (traffic-aware when Google provides it). */
  const [routeDriveSummary, setRouteDriveSummary] = useState(null);
  const isInitialized = useRef(false);
  
  // Vehicle orientation tracking
  const [vehicleOrientation, setVehicleOrientation] = useState(0);
  const previousPosition = useRef(null);
  
  // New state for dispatch functionality — do not hydrate Dispatched/Enroute/Loaded from
  // localStorage alone (after refresh jobDetails is empty until STARTM/JOBM), or the footer
  // shows "Dispatched" while the map says "No Assigned Trips". OffService/Busy/Clear persist
  // across a RELOAD of the same shift; they are cleared outright when the driver signs out
  // (comms.clearCurrentStatus), so a new driver never inherits them. Nothing cached means
  // 'Clear' — AutoDispatch's START corrects it either way.
  const [vehicleStatus, setVehicleStatus] = useState(() => {
    const v = comms.getCurrentVehicleStatus();
    if (v === 'Dispatched' || v === 'Enroute' || v === 'Loaded') return 'Clear';
    return v || 'Clear';
  });
  // 'Busy' | 'Clear' | null - a status change sent to AutoDispatch and not yet
  // answered. Purely for button feedback: the server stays the authority.
  const [pendingVehicleStatus, setPendingVehicleStatus] = useState(null);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [messages, setMessages] = useState([]);
  const [jobOffers, setJobOffers] = useState([]);
  // A server-rejected lifecycle action (TRIPERR). Completion is the one that matters:
  // the trip is NOT finished, so the driver has to see it and retry.
  const [tripActionError, setTripActionError] = useState(null);
  const [showJobOffer, setShowJobOffer] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ show: false, tripNumber: null });
  const [messageInput, setMessageInput] = useState('');
  

  const [showMessageInput, setShowMessageInput] = useState(false);
  
  // Messages state - moved up to be available immediately
  const [showMessages, setShowMessages] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const flashingInterval = useRef(null);

  // Job offer auto-accept timer tracking
  const jobOfferTimerRef = useRef(null);
  const [offerCountdown, setOfferCountdown] = useState(0);
  const countdownIntervalRef = useRef(null);

  // Toast state for schedule updates
  const [showScheduleToast, setShowScheduleToast] = useState(false);
  const toastCooldownRef = useRef(false);
  // Session-local map endpoint override used only while SoftMeter runs.
  const [softMeterRouteOverride, setSoftMeterRouteOverride] = useState(null);
  const activeMeterTripKeyRef = useRef('');

  // SoftMeter — GPS-based fare calculator
  // jobNumber → { pricing, meter, wait, disposition } from server job data
  const tripPricingRef = useRef({});

  /** Miles/mph by default; km/kmh when job or fleet sets a known metric flag. */
  const meterIsMetric = useMemo(() => {
    const job = jobDetails[0];
    const jobHint = firstDefinedFleetValue(job || {}, [
      'useMetric',
      'UseMetric',
      'isMetric',
      'IsMetric',
    ]);
    const fleetHint = firstDefinedFleetValue(fleetSettings || {}, [
      'useMetric',
      'UseMetric',
      'isMetric',
      'IsMetric',
      'metricDistance',
      'MetricDistance',
    ]);
    const raw =
      jobHint !== undefined && jobHint !== null && jobHint !== '' ? jobHint : fleetHint;
    if (raw === undefined || raw === null || raw === '') return false;
    return coerceFleetBoolean(raw);
  }, [jobDetails, fleetSettings]);

  const {
    fare: meterFare,
    distance: meterDistance,
    fareMode: meterFareMode,
    isRunning: meterRunning,
    meterConfig: meterMeterConfig,
    elapsedTime: meterElapsedTime,
    fareTimeSeconds: meterFareTimeSeconds,
    waitTime: meterWaitTime,
    waitCharge: meterWaitCharge,
    distanceCharge: meterDistanceCharge,
    timeCharge: meterTimeCharge,
    startMeter,
    stopMeter,
    pauseMeter,
    reEnableMeter,
    onLocationUpdate: meterLocationUpdate,
    calculateFinalFare,
    getFareBreakdown,
    getMeterStats,
    getRunningJobNumber,
  } = useSoftMeter({
    isMetric: meterIsMetric,
    onMeterTick: (stats) => {
      comms.sendMeter(stats);
    },
  });

  const meterRunningRef = useRef(false);
  useEffect(() => {
    meterRunningRef.current = meterRunning;
  }, [meterRunning]);

  // Route edits are session-local: clear when meter stops or active trip switches.
  useEffect(() => {
    const firstTrip = jobDetails[0] ? tripKey(jobDetails[0].jobNumber) : '';
    if (!meterRunning) {
      if (softMeterRouteOverride) setSoftMeterRouteOverride(null);
      activeMeterTripKeyRef.current = '';
      return;
    }
    if (
      activeMeterTripKeyRef.current &&
      firstTrip &&
      activeMeterTripKeyRef.current !== firstTrip &&
      softMeterRouteOverride
    ) {
      setSoftMeterRouteOverride(null);
    }
    activeMeterTripKeyRef.current = firstTrip;
  }, [meterRunning, jobDetails, softMeterRouteOverride]);

  const handleSetSoftMeterRouteOverride = useCallback((override) => {
    if (!meterRunningRef.current) return;
    const origin = normalizeMapLatLng(override?.origin);
    const destination = normalizeMapLatLng(override?.destination);
    if (!origin || !destination) return;
    if (!isValidMapPosition(origin) || !isValidMapPosition(destination)) return;
    setSoftMeterRouteOverride({
      origin,
      destination,
      destinationLabel: String(override?.destinationLabel || '').trim(),
    });
  }, []);

  const handleClearSoftMeterRouteOverride = useCallback(() => {
    setSoftMeterRouteOverride(null);
  }, []);

  /** While Flag Trip passenger-count modal is open, do not auto-start SoftMeter (STARTM/JOBM race). */
  const flagTripModalOpenRef = useRef(false);
  /** Resolver registered by the active FlagTripModal — invoked when JOBMF arrives. */
  const pendingFlagAckRef = useRef(null);

  const onFlagTripModalOpen = useCallback(() => {
    flagTripModalOpenRef.current = true;
  }, []);

  const onFlagTripModalDismiss = useCallback(() => {
    flagTripModalOpenRef.current = false;
    pendingFlagAckRef.current = null;
  }, []);

  /** FlagTripModal calls this with a resolver fn (or null to clear). */
  const registerPendingFlagAck = useCallback((resolver) => {
    pendingFlagAckRef.current = typeof resolver === 'function' ? resolver : null;
  }, []);

  const guardedStartMeter = useCallback(
    (jobNumber, rawMeter, rawWait) => {
      if (flagTripModalOpenRef.current) return;
      startMeter(jobNumber, rawMeter, rawWait);
    },
    [startMeter]
  );

  /**
   * Flag Trip succeeded for a specific jobNumber (arrived via JOBMF). Look up that trip's
   * pricing rather than blindly indexing jobDetails[0] — multiple trips may co-exist.
   *
   * NOTE: do NOT gate on jobDetailsRef.current — onJobDetails calls setJobDetails (async
   * React state) right before onFlagTrip fires the resolver that lands here, so the ref
   * hasn't been updated yet on this tick. tripPricingRef IS updated synchronously inside
   * onJobDetails, so it's safe to read here. JOBMF for a flag trip implies the trip is
   * Loaded by definition, so start the meter unconditionally if rates are usable.
   */
  const handleFlagTripModalClosed = useCallback((jobNumber) => {
    flagTripModalOpenRef.current = false;
    pendingFlagAckRef.current = null;
    if (jobNumber === undefined || jobNumber === null) return;
    const tk = tripKey(jobNumber);
    if (!tk) return;
    const p = tripPricingRef.current[tk];
    if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
      startMeter(jobNumber, p.meter, p.wait);
    }
  }, [startMeter]);

  // Configure OSRM Match service once per mount (fleet-driven base URL).
  useEffect(() => {
    configureOsrm({ fleetSettings });
  }, [fleetSettings]);

  // Fare confirmation modal state (shown before completing a metered trip)
  const [fareConfirmModal, setFareConfirmModal] = useState({ show: false, tripNumber: null, fareData: null });

  // Ref to hold latest socket callbacks — avoids stale closures
  const socketCallbacksRef = useRef({});

  // Track whether we're on a phone (vs. tablet/desktop)
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 768); // e.g. 768px as phone breakpoint
      }
    };
    handleResize(); // Run once on component mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);

  const allowFlag = fleetAllowsFlagTrip(fleetSettings);

  /**
   * Flag Trip is only visible when:
   *  - the fleet allows it,
   *  - there is no in-progress trip (Dispatched/Enroute/ArrivedOrigin/Loaded/ArrivedDestination),
   *  - the meter is not running.
   * Scheduled-but-not-started trips do NOT block flag eligibility.
   */
  const canFlag = !!(
    allowFlag &&
    !meterRunning &&
    !tripIsInProgress(currentTrip)
  );

  // Calculate vehicle orientation based on movement
  const calculateOrientation = (currentPos, previousPos) => {
    if (!previousPos) return 0;
    
    const deltaLat = currentPos.lat - previousPos.lat;
    const deltaLng = currentPos.lng - previousPos.lng;
    
    // Calculate bearing in degrees (0 = North, 90 = East, 180 = South, 270 = West)
    let bearing = Math.atan2(deltaLng, deltaLat) * (180 / Math.PI);
    
    // Normalize to 0-360 degrees
    if (bearing < 0) bearing += 360;
    
    return bearing;
  };

  const handleLocationUpdate = (newLocation) => {
    const normalized = normalizeMapLatLng(newLocation);
    if (!normalized) return;

    // Calculate orientation if we have a previous position
    if (previousPosition.current && isAccurate) {
      const orientation = calculateOrientation(normalized, previousPosition.current);
      setVehicleOrientation(orientation);
    }

    previousPosition.current = { ...normalized };

    // GoogleMap is controlled via `center` only. Do not also call map.panTo — that double-updates
    // the map and races Google's map_changed internals (minified "wE"/"pH" undefined errors).
    setCenter(normalized);
    // GPS sending is now handled automatically by GeolocationHandler
    // No need to send GPS manually here anymore
  };

  // Start flashing animation for messages button - moved up to be available immediately
  const startFlashing = useCallback(() => {
    if (flashingInterval.current) return; // Already flashing
    
    setIsFlashing(true);
    flashingInterval.current = setInterval(() => {
      setIsFlashing(prev => !prev);
    }, 500); // Flash every 500ms
  }, []);

  // Stop flashing animation - moved up to be available immediately
  const stopFlashing = useCallback(() => {
    if (flashingInterval.current) {
      clearInterval(flashingInterval.current);
      flashingInterval.current = null;
    }
    setIsFlashing(false);
    setHasUnreadMessages(false);
  }, []);

  // Clean up flashing interval on unmount
  useEffect(() => {
    return () => {
      if (flashingInterval.current) {
        clearInterval(flashingInterval.current);
      }
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Socket command handlers
  const socketCallbacks = {
    onStart: (status) => {
      // START is the authority and is also what AD sends when it REFUSES a
      // Busy/Clear request, so it resolves any in-flight button press.
      setPendingVehicleStatus(null);
      setVehicleStatus(status || 'Clear');
      comms.setCurrentVehicleStatus(status || 'Clear');
    },

    /**
     * START with no jobs — clear itinerary. Guard pricing wipe so a stray empty STARTM
     * during a metered trip doesn't drop the rate card mid-fare (Phase 3.12).
     */
    onStartNoJobs: () => {
      setPendingVehicleStatus(null);
      setJobDetails([]);
      if (meterRunningRef.current || currentTrip) {
        console.warn(
          'onStartNoJobs received while meter running / currentTrip present — keeping pricing for safety'
        );
        return;
      }
      setCurrentTrip(null);
      comms.setCurrentTripStatus(null);
      setVehicleStatus('Clear');
      comms.setCurrentVehicleStatus('Clear');
      tripPricingRef.current = {};
    },
    
    onStartM: (jobsArray) => {
      try {
        let jobs = JSON.parse(jobsArray);
        if (!Array.isArray(jobs)) {
          jobs = [jobs];
        }

        // Process all waypoints from all jobs
        let allWaypoints = [];
        let tripStatuses = {};
        
        jobs.forEach((rawJob) => {
          const job = {
            ...rawJob,
            info: rawJob.info ?? rawJob.Info,
            locations: rawJob.locations ?? rawJob.Locations,
          };
          if (!job.info || !job.locations) {
            console.warn('STARTM/STARTN job missing info or locations:', rawJob);
            return;
          }

          // Track trip status for vehicle status calculation
          tripStatuses[job.info.jobNumber] = job.info.status;

          const jnKey = tripKey(job.info.jobNumber);
          const { meter, wait, pricing, disposition, payment } = extractJobPricing(job);
          // Trip.Id (info.jobID) → jobNumber bucket, so a server push keyed by the GUID
          // (trip:paymentSettled, tripCollectionUrl…) lands on the displayed trip.
          if (job.info.jobID) tripIdIndexRef.current[tripIdKey(job.info.jobID)] = jnKey;
          // Recorded unconditionally — the pricing guard below skips trips with no
          // pricing rows, but those can still carry authoritative payment state.
          if (payment) setTripPayments((prev) => mergeIngestedPayment(prev, jnKey, job.info.jobID, payment));
          if (meter || wait || pricing.length) {
            tripPricingRef.current[jnKey] = {
              pricing,
              meter: meter || null,
              wait: wait || null,
              disposition,
            };
          }
          
          // Both legs' addresses, attached to BOTH waypoints, so a waypoint card can
          // show the full Pickup -> Dropoff pair rather than only its own leg.
          const pickupAddr = waypointAddressFromLeg(job.locations[0], { isDropoff: false });
          const dropAddr = waypointAddressFromLeg(job.locations[1], { isDropoff: true });
          const pickupLeg = normalizeMapLatLng(job.locations[0]);
          const tripLegAddresses = {
            tripPickupAddress1: pickupAddr.address1,
            tripPickupAddress2: pickupAddr.address2,
            tripDropoffAddress1: dropAddr.address1,
            tripDropoffAddress2: dropAddr.address2,
            // See the note on the other build site — lets the card reverse-geocode a
            // pickup address when only coords came through.
            tripPickupLat: pickupLeg?.lat,
            tripPickupLon: pickupLeg?.lng,
            // The trip's scheduled PICKUP time, carried on both legs so the dropoff
            // card can show it too (its own eta is the dropoff estimate, and on flag
            // trips is frequently absent entirely).
            tripPickupTime: formatScheduledPickupTime(job.info) || formatEtaTime(job.locations[0]?.eta),
          };

          // Add pickup waypoint only if trip is not already loaded or arrived at destination
          if (job.info.status !== 'Loaded' && job.info.status !== 'ArrivedDestination' && job.info.status !== 'Completed') {
            allWaypoints.push({
              type: 'Pickup',
              location: jobLegToLocationArray(job.locations[0]),
              timeStamp: job.locations[0].eta,
              time: formatEtaTime(job.locations[0].eta),
              paxName: job.info.name,
              jobNumber: job.info.jobNumber,
              // Trip GUID (JOB payload `info.jobID`). jobNumber is the short TripNo
              // ('60172'), but the server and RiderApp payment lookups match on the
              // GUID, so carry both — the QR handoff needs tripId.
              tripId: job.info.jobID,
              address1: pickupAddr.address1,
              address2: pickupAddr.address2,
              ...tripLegAddresses,
              account: job.info.account,
              phone: formatPhone(job.info.phone),
              wheelChair: !!job.info.seatingRequired?.wheelchair,
              completed: false,
              status: job.info.status
            });
          }

          // Always add dropoff waypoint (unless trip is completed)
          if (job.info.status !== 'Completed') {
            allWaypoints.push({
              type: 'Dropoff',
              location: jobLegToLocationArray(job.locations[1]),
              timeStamp: job.locations[1].eta,
              time: formatEtaTime(job.locations[1].eta),
              paxName: job.info.name,
              jobNumber: job.info.jobNumber,
              // Trip GUID (JOB payload `info.jobID`). jobNumber is the short TripNo
              // ('60172'), but the server and RiderApp payment lookups match on the
              // GUID, so carry both — the QR handoff needs tripId.
              tripId: job.info.jobID,
              address1: dropAddr.address1,
              address2: dropAddr.address2,
              ...tripLegAddresses,
              account: job.info.account,
              phone: formatPhone(job.info.phone),
              wheelChair: !!job.info.seatingRequired?.wheelchair,
              completed: false,
              status: job.info.status
            });
          }
        });

        allWaypoints = sortWaypointsDrivingOrder(allWaypoints);

        // Trips exist in payload but every leg was filtered out (e.g. all Completed) — avoid
        // vehicleStatus "Dispatched" with an empty waypoint list. Guard the pricing wipe so a
        // stale all-completed STARTM never drops the rate card mid-fare (Phase 3.12).
        if (allWaypoints.length === 0 && Object.keys(tripStatuses).length > 0) {
          console.warn(
            'STARTM/STARTN: trips in payload but no waypoints (e.g. all Completed) — clearing UI'
          );
          if (meterRunningRef.current || currentTrip) {
            console.warn(
              'all-completed STARTM with meter running / currentTrip present — keeping pricing for safety'
            );
            return;
          }
          tripPricingRef.current = {};
          setCurrentTrip(null);
          comms.setCurrentTripStatus(null);
          setJobDetails([]);
          setVehicleStatus('Clear');
          comms.setCurrentVehicleStatus('Clear');
          return;
        }

        // Find "active" job - the job with the next waypoint to visit
        const activeJob = allWaypoints.length > 0 ? {
          tripNumber: allWaypoints[0].jobNumber,
          status: allWaypoints[0].status,
          nextEventType: allWaypoints[0].type
        } : null;

        // Determine vehicle status based on trip statuses
        let vehicleStatus = 'Clear';
        
        if (Object.keys(tripStatuses).length === 0) {
          // No trips - status determined separately (Clear or Busy)
          vehicleStatus = 'Clear';
        } else if (Object.keys(tripStatuses).length === 1) {
          // Single trip - status based on trip status
          const tripStatus = Object.values(tripStatuses)[0];
          switch (tripStatus) {
            case 'Assigned':
            case 'ArrivedOrigin':
              vehicleStatus = 'Dispatched';
              break;
            case 'Enroute':
              vehicleStatus = 'Enroute';
              break;
            case 'Loaded':
            case 'ArrivedDestination':
              vehicleStatus = 'Loaded';
              break;
            default:
              vehicleStatus = 'Dispatched';
          }
        } else {
          // Multiple trips - find highest status
          const statusPriority = {
            'Assigned': 1,
            'Enroute': 2,
            'ArrivedOrigin': 3,
            'Loaded': 4,
            'ArrivedDestination': 5,
            'Completed': 6
          };
          
          let highestPriority = 0;
          let highestStatus = 'Clear';
          
          Object.values(tripStatuses).forEach(status => {
            const priority = statusPriority[status] || 0;
            if (priority > highestPriority) {
              highestPriority = priority;
              highestStatus = status;
            }
          });
          
          // Map highest trip status to vehicle status
          switch (highestStatus) {
            case 'Assigned':
            case 'ArrivedOrigin':
            case 'Enroute':
              vehicleStatus = highestStatus === 'Enroute' ? 'Enroute' : 'Dispatched';
              break;
            case 'Loaded':
            case 'ArrivedDestination':
              vehicleStatus = 'Loaded';
              break;
            default:
              vehicleStatus = 'Dispatched';
          }
        }

        setCurrentTrip(activeJob);
        if (activeJob) {
          comms.setCurrentTripStatus(activeJob);
        }
        
        setJobDetails(allWaypoints);
        setVehicleStatus(vehicleStatus);
        comms.setCurrentVehicleStatus(vehicleStatus);

        // Schedule already shows Loaded (e.g. after reconnect) — start meter without a second Pickup tap
        jobs.forEach((rawJob) => {
          const j = {
            ...rawJob,
            info: rawJob.info ?? rawJob.Info,
            locations: rawJob.locations ?? rawJob.Locations,
          };
          if (!j.info || String(j.info.status || '').trim().toLowerCase() !== 'loaded') return;
          const tk = tripKey(j.info.jobNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
            guardedStartMeter(j.info.jobNumber, p.meter, p.wait);
          }
        });
        
      } catch (e) {
        console.error('Error parsing STARTM jobs:', e);
      }
    },
    
    onLogon: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
      }
    },
    
    onLogoff: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('OffService');
        comms.setCurrentVehicleStatus('OffService');
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
      }
    },
    
    // BUSY/CLEAR acks. A non-'Ok' reply used to be dropped on the floor, which left
    // the button that sent it spinning forever; clear the pending flag either way so
    // the driver is never stuck without feedback. AutoDispatch follows any refusal
    // with START, and onStart/onStartNoJobs carry the authoritative status.
    onBusy: (result) => {
      setPendingVehicleStatus(null);
      if (result === 'Ok') {
        setVehicleStatus('Busy');
        comms.setCurrentVehicleStatus('Busy');
      }
    },

    onClear: (result) => {
      setPendingVehicleStatus(null);
      if (result === 'Ok') {
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
      }
    },
    
    onPing: () => {
      const c = normalizeMapLatLng(center);
      if (c) comms.sendGPS(c.lat, c.lng);
    },
    
    onMessage: (message) => {
      const newMessage = {
        id: Date.now(),
        text: message,
        timestamp: new Date(),
        type: 'received',
        from: 'Dispatch'
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      // Start flashing if messages modal is not open
      if (!showMessages) {
        setHasUnreadMessages(true);
        startFlashing();
      }
    },
    
    onJobOffer: (tripNumber, offerData) => {
      // driverTripRejection: fleet-configured accept window in seconds. An explicit 0 from
      // fleet config means "no UI, silent auto-accept" and is honored as-is; when the fleet
      // hasn't configured a value, default to a visible offer window instead of skipping
      // the modal entirely.
      //
      // ON_DEMAND_OFFER_BUFFER_SECONDS is that default. 10s was not enough time for a
      // driver to read the pickup/destination and decide, so the window auto-accepted
      // while they were still reading. 30s is the agreed buffer.
      const rejectionWindow = fleetSettings?.driverTripRejection ?? ON_DEMAND_OFFER_BUFFER_SECONDS;
      // How long the driver has to answer before the offer is handed back (or, for a
      // driverTripRejection of 0, the brief delay before the legacy silent accept).
      const offerWindowMs = rejectionWindow > 0 ? rejectionWindow * 1000 : 1000;
      
      // Show offer UI only if driverTripRejection > 0
      if (rejectionWindow > 0) {
        let offer = null;
        try {
          offer = typeof offerData === 'string' ? JSON.parse(offerData) : offerData;
        } catch (e) {
          // A malformed/truncated payload must NOT cost the driver the offer UI: the
          // auto-accept timer below still runs, so swallowing this used to take the trip
          // with no Accept/Reject ever shown. Show the offer with just the trip number -
          // the accept/reject calls only ever needed that.
          console.error('Error parsing job offer; showing offer without details:', e);
          offer = null;
        }
        // tripNumber last: the offer payload must not shadow the trusted argument
        // the accept/reject calls are keyed on.
        setPendingOffer({ ...(offer || {}), tripNumber });
        setShowJobOffer(true);

        // Start countdown display
        setOfferCountdown(rejectionWindow);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setOfferCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      
      // Schedule update notification
      if (!toastCooldownRef.current && rejectionWindow === 0) {
        setShowScheduleToast(true);
      }
      
      // No response = REJECT, not accept.
      //
      // An on-demand offer the driver never answered is not work they agreed to take.
      // Rejecting hands it back so AD can offer it to the next driver: dispatch.jobRejected
      // clears Vehicle/Driver and returns the trip to Scheduled, which is exactly the
      // re-offer path. If nobody accepts, it simply stays unassigned - no driver is ever
      // given a trip by silence.
      //
      // This also has to happen BEFORE the server's own offer timeout
      // (appVar.jobOfferTimeout, default 60s) so the driver's window is the one that
      // decides. The server's jobTimeout() does the same thing on its own if we are
      // slower, so the two agree either way.
      //
      // An explicit fleet driverTripRejection of 0 keeps its legacy meaning - no offer UI
      // and immediate silent accept - because that configuration is a deliberate
      // auto-assign fleet, not an unanswered offer.
      if (jobOfferTimerRef.current) clearTimeout(jobOfferTimerRef.current);
      jobOfferTimerRef.current = setTimeout(() => {
        if (rejectionWindow > 0) comms.rejectTrip(tripNumber);
        else comms.acceptTrip(tripNumber);
        // Close offer UI if open
        setShowJobOffer(false);
        setPendingOffer(null);
        setOfferCountdown(0);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        jobOfferTimerRef.current = null;
      }, offerWindowMs);
    },
    
    onJobOfferAutoAccepted: undefined, // No longer needed — auto-accept is handled in onJobOffer
    onJobOfferTimer: undefined, // No longer needed — timer is managed in onJobOffer
    
    onScheduleUpdate: () => {
      // Show toast only if not in cooldown
      if (!toastCooldownRef.current) {
        setShowScheduleToast(true);
      }
    },
    
    onJobDetails: (tripNumber, jobText) => {
      try {
        if (!jobText) {
          console.warn('No job details data received for trip:', tripNumber);
          return;
        }
        const rawJob = typeof jobText === 'string' ? JSON.parse(jobText) : jobText;
        const job = {
          ...rawJob,
          info: rawJob.info ?? rawJob.Info,
          locations: rawJob.locations ?? rawJob.Locations,
        };

        if (!job || !job.info || !job.locations) {
          console.warn('Invalid job details format for trip:', tripNumber);
          return;
        }

        const jnKey = tripKey(job.info.jobNumber);
        const { meter, wait, pricing, disposition, payment } = extractJobPricing(job);
        if (job.info.jobID) tripIdIndexRef.current[tripIdKey(job.info.jobID)] = jnKey;
          // Recorded unconditionally — the pricing guard below skips trips with no
          // pricing rows, but those can still carry authoritative payment state.
          if (payment) setTripPayments((prev) => mergeIngestedPayment(prev, jnKey, job.info.jobID, payment));
        if (meter || wait || pricing.length) {
          tripPricingRef.current[jnKey] = {
            pricing,
            meter: meter || null,
            wait: wait || null,
            disposition,
          };
        }

        // Phase 3.11 — JOBM for a *different* trip while meter still runs:
        // do not stop or replace the live meter; defer the new trip's meter until completion.
        if (meterRunningRef.current) {
          const runningJn = getRunningJobNumber();
          if (runningJn && jnKey && tripKey(runningJn) !== jnKey) {
            console.error(
              `Meter still running for trip ${runningJn} — settle before starting trip ${job.info.jobNumber}`
            );
          }
        }

        // Build pickup/dropoff waypoints from the job (same logic as onStartM)
        const newWaypoints = [];

        // Both legs' addresses, attached to BOTH waypoints, so a waypoint card can show
        // the full Pickup -> Dropoff pair. Guarded because a flag trip can arrive with
        // only an origin leg (see the dropoff note below).
        const pickupAddr = job.locations[0]
          ? waypointAddressFromLeg(job.locations[0], { isDropoff: false })
          : null;
        const dropAddr = job.locations[1]
          ? waypointAddressFromLeg(job.locations[1], { isDropoff: true })
          : null;
        const pickupLeg = job.locations[0] ? normalizeMapLatLng(job.locations[0]) : null;
        const tripLegAddresses = {
          tripPickupAddress1: pickupAddr?.address1,
          tripPickupAddress2: pickupAddr?.address2,
          tripDropoffAddress1: dropAddr?.address1,
          tripDropoffAddress2: dropAddr?.address2,
          // Pickup coords travel with the waypoint so the card can reverse-geocode a
          // display address when dispatch sent coords but no street text (common on
          // flag trips, where the origin leg often carries no resolved address).
          tripPickupLat: pickupLeg?.lat,
          tripPickupLon: pickupLeg?.lng,
          // Scheduled PICKUP time — see the note on the other build site.
          tripPickupTime: formatScheduledPickupTime(job.info) || formatEtaTime(job.locations[0]?.eta),
        };

        // Add pickup waypoint only if trip is not already loaded or arrived at destination
        if (
          job.info.status !== 'Loaded' &&
          job.info.status !== 'ArrivedDestination' &&
          job.info.status !== 'Completed' &&
          job.locations[0]
        ) {
          newWaypoints.push({
            type: 'Pickup',
            location: jobLegToLocationArray(job.locations[0]),
            timeStamp: job.locations[0].eta,
            time: formatEtaTime(job.locations[0].eta),
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            // Trip GUID (JOB payload `info.jobID`). jobNumber is the short TripNo
            // ('60172'), but the server and RiderApp payment lookups match on the
            // GUID, so carry both — the QR handoff needs tripId.
            tripId: job.info.jobID,
            address1: pickupAddr.address1,
            address2: pickupAddr.address2,
            ...tripLegAddresses,
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        // Add dropoff waypoint (unless trip is completed). Flag trips arrive without a
        // destination — `job.locations[1]` is undefined until dispatch (or the driver)
        // assigns a dropoff, so we skip the dropoff waypoint in that case.
        if (job.info.status !== 'Completed' && job.locations[1]) {
          newWaypoints.push({
            type: 'Dropoff',
            location: jobLegToLocationArray(job.locations[1]),
            timeStamp: job.locations[1].eta,
            time: formatEtaTime(job.locations[1].eta),
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            // Trip GUID (JOB payload `info.jobID`). jobNumber is the short TripNo
            // ('60172'), but the server and RiderApp payment lookups match on the
            // GUID, so carry both — the QR handoff needs tripId.
            tripId: job.info.jobID,
            address1: dropAddr.address1,
            address2: dropAddr.address2,
            ...tripLegAddresses,
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        // Flag trip arrives JOBMF as Loaded with only an Origin location — no pickup
        // (already loaded) and no dropoff (passenger-determined). Synthesize a Dropoff
        // waypoint with the "Passenger Determined" placeholder so the trip binds to
        // jobDetails / currentTrip and CurrentWaypoint can display it as Loaded.
        if (
          newWaypoints.length === 0 &&
          job.info.status === 'Loaded' &&
          job.locations[0] &&
          !job.locations[1]
        ) {
          newWaypoints.push({
            type: 'Dropoff',
            location: [0, 0],
            // There is no dropoff leg to take a time from, so use the trip's scheduled
            // pickup time — for a flag trip that IS its scheduled time. Previously
            // hardcoded empty, which left the card's time slot blank.
            timeStamp: job.locations[0].eta,
            time: formatScheduledPickupTime(job.info) || formatEtaTime(job.locations[0].eta),
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            // Trip GUID (JOB payload `info.jobID`). jobNumber is the short TripNo
            // ('60172'), but the server and RiderApp payment lookups match on the
            // GUID, so carry both — the QR handoff needs tripId.
            tripId: job.info.jobID,
            address1: PASSENGER_DETERMINED_DROP,
            address2: '',
            // Pickup is known (origin leg exists); the dropoff genuinely isn't yet.
            ...tripLegAddresses,
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        if (newWaypoints.length > 0) {
          setJobDetails(prev => {
            const tk = tripKey(tripNumber);
            const filtered = prev.filter(wp => tripKey(wp.jobNumber) !== tk);
            const merged = [...filtered, ...newWaypoints];
            return sortWaypointsDrivingOrder(merged);
          });

          // Update current trip and vehicle status
          const activeJob = {
            tripNumber: job.info.jobNumber,
            status: job.info.status,
            nextEventType: newWaypoints[0].type
          };
          setCurrentTrip(activeJob);
          comms.setCurrentTripStatus(activeJob);
          const st = String(job.info.status || '').trim().toLowerCase();
          if (st === 'loaded' || st === 'arriveddestination') {
            setVehicleStatus('Loaded');
            comms.setCurrentVehicleStatus('Loaded');
          } else if (st === 'enroute') {
            setVehicleStatus('Enroute');
            comms.setCurrentVehicleStatus('Enroute');
          } else {
            setVehicleStatus('Dispatched');
            comms.setCurrentVehicleStatus('Dispatched');
          }
        }

        if (String(job.info.status || '').trim().toLowerCase() === 'loaded') {
          const tk = tripKey(job.info.jobNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
            guardedStartMeter(job.info.jobNumber, p.meter, p.wait);
          }
        }
      } catch (e) {
        console.error('Error parsing JOBM job details:', e);
      }
    },
    
    /**
     * JOBMF after FLAGM — same job JSON as JOBM. Process via onJobDetails to merge
     * trips/pricing, then resolve any pending FlagTripModal awaiter with this jobNumber
     * (Phase 1.3 — JOBMF is the canonical FLAGM ack, not STARTM).
     */
    onFlagTrip: (tripNumber, jobText) => {
      const fn = socketCallbacksRef.current.onJobDetails;
      if (typeof fn === 'function') {
        fn(tripNumber, jobText);
      }
      const resolver = pendingFlagAckRef.current;
      if (typeof resolver === 'function') {
        pendingFlagAckRef.current = null;
        try {
          resolver(tripNumber);
        } catch (err) {
          console.error('FlagTripModal pending ack resolver threw:', err);
        }
      }
    },

    onDeleteJob: (tripNumber) => {
      const tk = tripKey(tripNumber);
      setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== tk));
      if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
        // Bind stop to this jobNumber — no-op if meter is running for a different trip.
        stopMeter(tripNumber);
      }
      delete tripPricingRef.current[tk];
    },
    
    onSetDriver: (driverData) => {
      setDriverDetails(driverData);

      if (driverData && driverData.fixedRoute) {
        // Driver has fixed route — redirection handled elsewhere; bail out of the
        // schedule break processing path.
        return;
      }
      
      // Process route break information if present
      if (driverData.route && driverData.route.BreakStart && driverData.route.BreakEnd) {
        const breakStart = new Date(driverData.route.BreakStart);
        const breakEnd = new Date(driverData.route.BreakEnd);
        const currentTime = new Date();
        
        // Only add break to itinerary if it hasn't already ended
        if (currentTime < breakEnd) {
          // Create break waypoint object
          const breakWaypoint = {
            type: 'Break',
            timeStamp: breakStart.getTime() / 1000, // Convert to Unix timestamp
            time: breakStart.toLocaleTimeString(),
            startTime: breakStart.toLocaleTimeString(),
            endTime: breakEnd.toLocaleTimeString(),
            paxName: 'Driver Break',
            jobNumber: 'BREAK',
            address1: `Break: ${breakStart.toLocaleTimeString()} - ${breakEnd.toLocaleTimeString()}`,
            address2: 'Scheduled Break Time',
            account: 'System',
            phone: '',
            wheelChair: false,
            completed: false,
            status: 'Scheduled',
            isBreak: true,
            location: [0, 0] // No specific location for break
          };
          
          // Insert break into existing job details at correct time position
          setJobDetails(prevJobs => {
            const updatedJobs = [...prevJobs, breakWaypoint];
            return sortWaypointsDrivingOrder(updatedJobs);
          });
        }
      }
    },
    
    onSetInAppPayment: (tripNumber) => {
      setPaymentModal({ show: true, tripNumber });
    },
    
    onSetLoaded: (tripNumber) => {
      const tk = tripKey(tripNumber);
      setCurrentTrip((prev) => {
        if (!prev || tripKey(prev.tripNumber) !== tk) return prev;
        const next = { ...prev, status: 'Loaded' };
        comms.setCurrentTripStatus(next);
        return next;
      });
      const p = tripPricingRef.current[tk];
      if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
        guardedStartMeter(tripNumber, p.meter, p.wait);
      }
    },
    
    /**
     * AD rejected a trip lifecycle action (COMPM/EPMTM/...).
     *
     * Live COMPM has no success acknowledgement, and 'complete' optimistically drops the
     * waypoint from the local list — so a rejected completion used to leave the driver
     * looking at "No Assigned Trips" while the trip was still ArrivedDestination on the
     * server. Nothing surfaced the error because this callback did not exist: comms.js
     * routed TRIPERR only to the flag-trip modal handler.
     *
     * Show it, and put the trip back on screen so 'Complete' can be retried.
     */
    onTripError: (errObj) => {
      const action = String(errObj?.action || '').toUpperCase();
      if (action && action !== 'COMPM' && action !== 'EPMTM') return;
      const code = String(errObj?.code || '');
      const tripNumber = errObj?.tripNumber != null ? String(errObj.tripNumber) : '';
      console.error('Trip action rejected by dispatch:', action, code, errObj?.message);
      setTripActionError({
        tripNumber,
        code,
        message: String(errObj?.message || 'Dispatch rejected the request.'),
      });
      // The optimistic removal was wrong — this trip is not finished. AD re-pushes the
      // trip's JOBM on its next dispatch pass, which restores it; the banner keeps the
      // driver from walking away in the meantime, and Sender.sendStart() has already put
      // the vehicle back to its real status.
    },

    onSetCompleted: (tripNumber) => {
      // Bind stop to this jobNumber so a stray COMPM for a different trip never
      // interrupts the active fare (Phase 3.10).
      stopMeter(tripNumber);
      const tk = tripKey(tripNumber);
      delete tripPricingRef.current[tk];
      if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
      }
    },

    // Hosted card page link for a requested collection. "Link created" only — it
    // never unlocks completion (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md).
    onTripCollectionUrl: (data) => {
      if (!data) return;
      if (data.error) {
        setCollectionState({ tripId: data.tripId || null, url: null, error: data.error });
      } else if (data.url) {
        setCollectionState({ tripId: data.tripId || null, url: data.url, error: null });
      }
    },

    // Authoritative Paid push from AutoDispatch's Drivers room. Fires for HBSS webhooks,
    // RiderApp QR payments this app never requested, and wallet settlements. Matched by
    // the exact Trip.Id and applied immediately — even while the trip is still Loaded.
    onTripPaymentSettled: (data) => {
      if (!data?.tripId) return;
      applySettlementUnverified(data.tripId, data);
    },

    // Terminal outcome for a hosted collection attempt. success:true is a settlement
    // (same as trip:paymentSettled); success:false leaves the trip active with the
    // server's reason (INVALID_AMOUNT, PRICE_NOT_FINAL, PAYMENT_PENDING, ALREADY_PAID,
    // NO_PAYMENT_REQUIRED, PAYMENT_OWNERSHIP_MISMATCH, expiry…).
    onTripCollectionResult: (data) => {
      if (!data) return;
      if (data.success === true) {
        // Confirm with the payment authority before showing "Payment complete" — a
        // hosted page that was opened and abandoned must not read as collected.
        confirmSettlement(data.tripId, { paymentMethod: 'Credit Card', transactionId: data.transactionId, paymentAmount: data.paymentAmount });
      } else if (data.success === false) {
        // INVALID_AMOUNT / INVALID_ITEMS mean the submitted bundle disagreed with the
        // server — not that the fare is immutable (the exact-fare equality rule is
        // gone). The contract is to refresh the fare and items and let the driver
        // review before retrying, so pull authoritative state and drop the local edits
        // that were just rejected.
        const code = String(data.code || '').toUpperCase();
        const needsReview = code === 'INVALID_AMOUNT' || code === 'INVALID_ITEMS';
        if (needsReview) {
          setChargeEdits({});
          const wp = jobDetailsRef.current?.find(
            (j) => tripIdKey(j.tripId) === tripIdKey(data.tripId)
          );
          if (wp) refreshTripPaymentState(wp);
        }
        setCollectionState({
          tripId: data.tripId || null,
          url: null,
          error: data.error
            || (needsReview ? 'Dispatch rejected these charges. Review the updated amounts and try again.' : null)
            || (data.status === 'Expired' ? 'Collection link expired — try again.' : 'Payment was not completed.'),
        });
      }
    },

    // View / Text receipt reply. A text counts as sent only when Twilio's messageSid
    // is present; any failure leaves the Paid state untouched.
    onTripReceiptResult: (data) => {
      if (!data) return;
      if (data.success) {
        setReceiptState((prev) => ({
          loading: false,
          url: data.receiptUrl || null,
          error: null,
          messageSid: data.messageSid || null,
          sentTo: data.messageSid ? prev.sentTo : null,
        }));
      } else {
        setReceiptState((prev) => ({ ...prev, loading: false, error: data.error || data.message || 'Receipt request failed.' }));
      }
    }
  };

  // Keep the ref synced with the latest callbacks on every render
  // so socket handlers always call current closures (no stale state)
  socketCallbacksRef.current = socketCallbacks;

  useEffect(() => {
    if (!userSettings) {
      // User settings are passed as props, no need to load from localStorage
      return;
    }
  }, [userSettings]);

  useEffect(() => {
    if (!fleetSettings) return;
    const mapKey =
      fleetSettings.googleApiKey ||
      fleetSettings.googleAPIKey ||
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      null;
    setGoogleApiKey(mapKey || null);
  }, [fleetSettings]);

  useEffect(() => {
    const initialize = async () => {
      if (!fleetSettings) return;

      let localSocket =null;
      
      // Build a stable proxy that delegates to socketCallbacksRef.current
      // This way socket handlers always call the latest closures, not stale ones
      const stableCallbacks = new Proxy({}, {
        get: (target, prop) => {
          const fn = socketCallbacksRef.current[prop];
          if (typeof fn === 'function') {
            return (...args) => fn(...args);
          }
          return fn;
        },
        ownKeys: () => Object.keys(socketCallbacksRef.current),
        getOwnPropertyDescriptor: (target, prop) => {
          if (prop in socketCallbacksRef.current) {
            return { configurable: true, enumerable: true, writable: true, value: socketCallbacksRef.current[prop] };
          }
        },
        has: (target, prop) => prop in socketCallbacksRef.current
      });
      
      // Pass socket callbacks to initializeSocket instead of setting up here
      const cleanup = await initializeSocket(localSocket, setJobDetails, setDriverDetails, userSettings, fleetSettings, stableCallbacks);
      return cleanup;
    };

    initialize();
  }, [ fleetSettings, userSettings]);

  const displayTripInfo = (jobNumber) => {
    const trip = jobDetails.filter((job) => job.jobNumber === jobNumber);
    setSelectedTrip(trip);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTrip(null);
  };

  const geolocationSuccess = (position) => {
    const { latitude, longitude } = position.coords;
    const normalized =
      readGoogleLatLng({ lat: latitude, lng: longitude }) ??
      normalizeMapLatLng({ lat: latitude, lng: longitude });
    if (normalized) setCenter(normalized);
  };

  const handleDirectionsCalculated = (durationSeconds, meta = {}) => {
    if (jobDetails[0]) {
      const now = new Date();
      const eta = new Date(now.getTime() + durationSeconds * 1000);
      setCurrentEta(eta.toLocaleTimeString());
      const minutes = Math.max(1, Math.round(durationSeconds / 60));
      setRouteDriveSummary({
        minutes,
        usesTraffic: !!meta.usedTrafficDuration,
        distanceMeters:
          typeof meta.distanceMeters === 'number' && meta.distanceMeters > 0
            ? meta.distanceMeters
            : null,
        routePreference:
          meta.routePreference === 'shortest' ? 'shortest' : 'fastest',
      });
    }
  };

  const activeJobNumber = jobDetails[0]?.jobNumber;
  useEffect(() => {
    setRouteDriveSummary(null);
    setCurrentEta(null);
  }, [activeJobNumber]);

  const handleRequestDirections = (requestCallback, showDirectionsCallback) => {
    const vehicleOrigin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    const overrideDestination = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (
      meterRunning &&
      vehicleOrigin &&
      overrideDestination &&
      isValidMapPosition(vehicleOrigin) &&
      isValidMapPosition(overrideDestination)
    ) {
      requestCallback({
        origin: vehicleOrigin,
        destination: overrideDestination,
        travelMode: 'DRIVING',
      });
      showDirectionsCallback(true);
      return;
    }

    const origin = normalizeMapLatLng(center);
    if (jobDetails.length > 0 && origin && isAccurate) {
      // Build the complete itinerary starting with vehicle location
      const waypoints = [];
      
      // Add all waypoints from jobDetails in order - only include valid DirectionsWaypoint properties
      jobDetails.forEach((job) => {
        if (!Array.isArray(job.location) || job.location.length < 2) return;
        const loc = normalizeMapLatLng({
          lat: job.location[0],
          lng: job.location[1],
        });
        if (!loc) return;
        waypoints.push({
          location: loc,
          stopover: true,
        });
      });

      if (waypoints.length === 0) {
        console.warn('LiveDispatch: directions skipped — no valid waypoint coordinates');
        return;
      }

      // OSRM-only routing for cumulative ETAs across all jobs. Returns null on any
      // failure (no osrmUrl, network error, empty result) so the UI silently keeps
      // its previous ETA values.
      const osrmUrl =
        fleetSettings?.osrmUrl ||
        fleetSettings?.osrmBaseUrl ||
        fleetSettings?.OsrmBaseUrl ||
        null;

      // Issue one OSRM request per job to get per-leg distance/duration. This matches
      // the prior Google semantics where each waypoint produced its own leg. Failures
      // (null result) leave the corresponding job ETA at zero so cumulatives still flow.
      const legPromises = [];
      let prevPoint = origin;
      for (const wp of waypoints) {
        legPromises.push(
          getDirections({ origin: prevPoint, destination: wp.location, osrmUrl })
        );
        prevPoint = wp.location;
      }

      Promise.all(legPromises)
        .then((legResults) => {
          if (!legResults.some((r) => r && r.path && r.path.length > 0)) return;

          const updatedJobDetails = [...jobDetails];
          let cumulativeDistance = 0;
          let cumulativeDuration = 0;

          legResults.forEach((leg, legIndex) => {
            if (legIndex >= updatedJobDetails.length) return;

            if (leg && typeof leg.distance === 'number' && typeof leg.duration === 'number') {
              const distanceMeters = leg.distance;
              const durationSeconds = leg.duration;
              const distanceMiles = (distanceMeters * 0.000621371).toFixed(2);
              const durationMinutes = Math.round(durationSeconds / 60);

              cumulativeDistance += distanceMeters;
              cumulativeDuration += durationSeconds;

              updatedJobDetails[legIndex] = {
                ...updatedJobDetails[legIndex],
                etaDistance: distanceMiles,
                etaDuration: durationMinutes,
                cumulativeDistance: (cumulativeDistance * 0.000621371).toFixed(2),
                cumulativeDuration: Math.round(cumulativeDuration / 60),
                estimatedArrival: new Date(Date.now() + cumulativeDuration * 1000).toLocaleTimeString(),
              };
            } else {
              // Co-located stop or OSRM failed for this leg — assume zero-length leg so
              // cumulative values still progress correctly.
              updatedJobDetails[legIndex] = {
                ...updatedJobDetails[legIndex],
                etaDistance: '0.00',
                etaDuration: 0,
                cumulativeDistance: (cumulativeDistance * 0.000621371).toFixed(2),
                cumulativeDuration: Math.round(cumulativeDuration / 60),
                estimatedArrival: new Date(Date.now() + cumulativeDuration * 1000).toLocaleTimeString(),
              };
            }
          });

          setJobDetails(updatedJobDetails);
        })
        .catch(() => {
          // Silent failure — keep existing ETAs.
        });

      // Trigger the map polyline display for the first segment (vehicle -> first stop)
      const displayRequest = {
        origin,
        destination: waypoints[0].location,
        travelMode: 'DRIVING',
      };
      requestCallback(displayRequest);
      showDirectionsCallback(true);
    } else {
      console.error('Cannot request directions: Missing waypoints, valid GPS center, or accuracy.');
    }
  };

  // Dispatch action handlers
  const handleAcceptTrip = () => {
    if (pendingOffer) {
      // Cancel auto-accept timer since driver is manually accepting
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
        jobOfferTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      comms.acceptTrip(pendingOffer.tripNumber);
      setShowJobOffer(false);
      setPendingOffer(null);
      setOfferCountdown(0);
    }
  };

  const handleRejectTrip = () => {
    if (pendingOffer) {
      // Cancel auto-accept timer since driver is manually rejecting
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
        jobOfferTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      comms.rejectTrip(pendingOffer.tripNumber);
      setShowJobOffer(false);
      setPendingOffer(null);
      setOfferCountdown(0);
    }
  };

  const handleTripStatusChange = (tripNumber, newStatus) => {
    switch (newStatus) {
      case 'Enroute':
        comms.setEnroute(tripNumber);
        break;
      case 'ArrivedOrigin':
        comms.setArrivedOrigin(tripNumber);
        break;
      case 'Loaded':
        comms.setLoaded(tripNumber);
        {
          const tk = tripKey(tripNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter)) {
            guardedStartMeter(tripNumber, p.meter, p.wait);
          } else {
            console.warn(
              'SoftMeter: no usable meter config for trip',
              tripNumber,
              '- ensure STARTM/JOBM includes meter rates; meter will start automatically when rates arrive.'
            );
          }
        }
        break;
      case 'ArrivedDestination':
        comms.setArrivedDestination(tripNumber);
        break;
      case 'NoShow':
        comms.setNoShow(tripNumber);
        stopMeter(tripNumber);
        break;
      case 'Completed': {
        if (meterRunning) {
          // Pause (don't stop) the meter and show fare confirmation modal.
          // pauseMeter keeps meterRef.current alive so handleFareCancel → reEnableMeter
          // can resume the same meter. handleFareConfirm calls stopMeter to finalize.
          const fareData = getFareBreakdown();
          const stats = getMeterStats();
          pauseMeter(tripNumber);
          if (fareData) {
            setFareConfirmModal({ show: true, tripNumber, fareData, stats });
            return; // Don't update trip state yet — modal handles it
          }
        }
        comms.setCompleted(tripNumber);
        break;
      }
      default:
        console.warn('Unknown trip status:', newStatus);
    }

    setCurrentTrip((prev) => {
      if (!prev || tripKey(prev.tripNumber) !== tripKey(tripNumber)) {
        return prev;
      }
      const next = { ...prev, status: newStatus };
      comms.setCurrentTripStatus(next);
      return next;
    });
  };

  const handleVehicleStatusChange = (newStatus) => {
    switch (newStatus) {
      case 'Busy':
        setPendingVehicleStatus('Busy');
        comms.setBusy();
        break;
      case 'Clear':
        setPendingVehicleStatus('Clear');
        comms.setClear();
        break;
      case 'Logoff':
        comms.logoff();
        break;
      default:
        console.warn('Unknown vehicle status:', newStatus);
        return;
    }
    // AutoDispatch answers BUSY/CLEAR with an ack, and answers a refusal with START.
    // If neither arrives (dropped socket, stalled inbound queue) the button must still
    // become pressable again rather than looking permanently dead.
    if (newStatus === 'Busy' || newStatus === 'Clear') {
      window.setTimeout(() => {
        setPendingVehicleStatus((pending) => (pending === newStatus ? null : pending));
      }, VEHICLE_STATUS_ACK_TIMEOUT_MS);
    }
  };

  const handleShowMessages = () => {
    setShowMessages(true);
    stopFlashing(); // Stop flashing when modal opens
  };

  const handleCloseMessages = () => {
    setShowMessages(false);
  };

  const handleSendMessage = (messageText) => {
    comms.sendMessage(messageText);
    
    // Add to local messages
    const newMessage = {
      id: Date.now(),
      text: messageText,
      timestamp: new Date(),
      type: 'sent',
      from: driverDetails?.name || 'Driver'
    };
    
    setMessages(prev => [...prev, newMessage]);
  };

  const handleDismissScheduleToast = () => {
    setShowScheduleToast(false);
    toastCooldownRef.current = true;
    
    // Reset cooldown after 5 seconds
    setTimeout(() => {
      toastCooldownRef.current = false;
    }, 5000);
  };

  const handleMarkAsRead = () => {
    setHasUnreadMessages(false);
    stopFlashing();
  };

  const handleWaypointAction = (action, jobNumber, waypointType) => {
    switch (action) {
      case 'arrive':
        if (waypointType === 'Dropoff') {
          handleTripStatusChange(jobNumber, 'ArrivedDestination');
        } else {
          handleTripStatusChange(jobNumber, 'ArrivedOrigin');
        }
        break;
      case 'load':
        handleTripStatusChange(jobNumber, 'Loaded');
        // Remove the completed pickup waypoint from jobDetails (case-insensitive type)
        {
          const jk = tripKey(jobNumber);
          setJobDetails((prev) =>
            sortWaypointsDrivingOrder(
              prev.filter(
                (job) =>
                  !(tripKey(job.jobNumber) === jk && waypointTypeKey(job.type) === 'pickup')
              )
            )
          );
        }
        break;
      case 'complete':
        handleTripStatusChange(jobNumber, 'Completed');
        // Waypoint removal happens in handleTripStatusChange (immediate)
        // or handleFareConfirm (metered trip after modal)
        if (!meterRunning) {
          const jk = tripKey(jobNumber);
          setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== jk));
        }
        break;
      case 'noshow':
        handleTripStatusChange(jobNumber, 'NoShow');
        {
          const jk = tripKey(jobNumber);
          setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== jk));
        }
        break;
      case 'callout':
        comms.triggerCallout(jobNumber);
        break;
      default:
        console.warn('Unknown waypoint action:', action);
    }
  };

  const handlePaymentUpdate = (paymentType) => {
    if (paymentModal.tripNumber) {
      comms.updatePayment(paymentModal.tripNumber, paymentType);
      setPaymentModal({ show: false, tripNumber: null });
    }
  };

  /**
   * Driver confirmed the fare. Build PRICE line items for meter, wait, and any each-typed
   * extras (with driver-adjusted qty). Tip / toll / surcharge ride alongside as a separate
   * disposition block so the server records them in disposition.price.* columns rather than
   * as ad-hoc pricing rows (Phase 5.21).
   *
   * @param {number} adjustedTotal       Total the driver confirmed (may equal calculated total).
   * @param {object} [dispositionUpdates] `{ tip, toll, surcharge }` from the modal.
   * @param {Array}  [usedExtras]         Extras the driver applied (qty > 0).
   */
  const handleFareConfirm = (adjustedTotal, dispositionUpdates = null, usedExtras = []) => {
    const { tripNumber, fareData, stats, purpose } = fareConfirmModal;
    const pricingData = tripPricingRef.current[tripKey(tripNumber)];

    const priceItems = [];
    if (pricingData?.meter) {
      priceItems.push({
        id: pricingData.meter.id || '',
        rate: adjustedTotal,
        name: 'Meter',
        type: 'meter',
      });
    }
    if (fareData?.waitCharge > 0 && pricingData?.wait) {
      // Server's PRICE handler (lib/dispatch.js jobUpdatePriceItems) zeroes out any
      // flat-rate (no-qty) item whose name contains "Wait". To bypass that branch we
      // send qty so the server takes the qty path: TripAmount = ItemAmount * qty.
      // Since ItemAmount on the server == our wait.timeUnitRate (same Rate_RateItem
      // source), qty = waitCharge / timeUnitRate yields TripAmount ≈ waitCharge.
      // Round qty to 4 decimals so the inline SQL doesn't pick up float noise like
      // "22.500000000000004" from divisions like 6.75 / 0.30.
      const unitRate = Number(pricingData.wait.timeUnitRate) || 0;
      const rawQty = unitRate > 0 ? fareData.waitCharge / unitRate : 1;
      const qty = Math.round(rawQty * 10000) / 10000;
      priceItems.push({
        id: pricingData.wait.id || '',
        rate: fareData.waitCharge,
        qty,
        name: 'Wait Time',
        type: 'wait',
      });
    }
    if (Array.isArray(usedExtras)) {
      for (const ex of usedExtras) {
        const qty = Number(ex?.qty);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        priceItems.push({
          id: ex.id || '',
          rate: Number(ex.rate) || 0,
          qty,
          name: ex.name || 'Extra',
          type: 'each',
        });
      }
    }

    if (stats) {
      comms.sendMeterDistance(tripNumber, stats.totalDistance);
    }

    if (purpose === 'payment') {
      // Payment-first (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md): PRICE → PRICE|Ok → collect →
      // COMPM only after settlement. The trip stays on screen; the meter is finalized
      // once dispatch acknowledges the fare.
      setFareConfirmModal({ show: false, tripNumber: null, fareData: null, stats: null });
      startFareFinalize(tripNumber, priceItems, adjustedTotal);
      return;
    }

    comms.completeWithPricing(tripNumber, priceItems, dispositionUpdates);

    // Now that the trip is being completed for real, fully tear down the meter
    // (was only paused before the modal so Cancel could resume it).
    stopMeter(tripNumber);

    setFareConfirmModal({ show: false, tripNumber: null, fareData: null, stats: null });
    const tk = tripKey(tripNumber);
    delete tripPricingRef.current[tk];
    setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== tk));
    if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
      setCurrentTrip(null);
      comms.setCurrentTripStatus(null);
      setVehicleStatus('Clear');
      comms.setCurrentVehicleStatus('Clear');
    }
  };

  const handleFareCancel = () => {
    const tn = fareConfirmModal?.tripNumber;
    reEnableMeter(tn);
    setFareConfirmModal({ show: false, tripNumber: null, fareData: null, stats: null });
  };

  // Soft meter state object passed down to CurrentWaypoint via WaypointMap
  const softMeterState = meterRunning ? {
    fare: meterFare,
    fareMode: meterFareMode,
    distance: meterDistance,
    isRunning: meterRunning,
    elapsedTime: meterElapsedTime,
    fareTimeSeconds: meterFareTimeSeconds,
    waitTime: meterWaitTime,
    waitCharge: meterWaitCharge,
    distanceCharge: meterDistanceCharge,
    timeCharge: meterTimeCharge,
    flagRate: Number(meterMeterConfig?.flagRate) || 0,
  } : null;

  const firstWp = jobDetails[0];
  const firstTripKey = firstWp ? tripKey(firstWp.jobNumber) : '';
  const firstTripPricing = firstTripKey ? tripPricingRef.current[firstTripKey] : null;
  const meterRatesReady =
    !!(firstTripPricing?.meter && hasUsableMeterRates(firstTripPricing.meter));

  // GPS geofence auto-arrive (02-trip-lifecycle.md). Only applies to the
  // current Pickup waypoint before it's been marked arrived — once the server
  // confirms ArrivedOrigin/Loaded/etc. this naturally stops re-firing because
  // firstWp.status changes (and useGeofenceArrival also latches per resetKey).
  const geofenceEnabled =
    !!firstWp &&
    waypointTypeKey(firstWp.type) === 'pickup' &&
    !['arrivedorigin', 'loaded', 'arriveddestination', 'completed', 'noshow'].includes(
      String(firstWp.status || '').trim().toLowerCase()
    );
  const geofenceRadiusMeters =
    Number(fleetSettings?.geofenceArriveRadiusMeters) > 0
      ? Number(fleetSettings.geofenceArriveRadiusMeters)
      : 150;
  const [autoArriveBanner, setAutoArriveBanner] = useState(false);

  useGeofenceArrival({
    enabled: geofenceEnabled,
    center,
    isAccurate,
    targetLocation: firstWp?.location,
    resetKey: firstWp ? `${firstTripKey}:${waypointTypeKey(firstWp.type)}` : null,
    radiusMeters: geofenceRadiusMeters,
    onArrive: () => {
      if (!firstWp) return;
      handleWaypointAction('arrive', firstWp.jobNumber, firstWp.type);
      playArrivalChime();
      if (navigator.vibrate) navigator.vibrate(200);
      setAutoArriveBanner(true);
      setTimeout(() => setAutoArriveBanner(false), 5000);
    },
  });

  // Mid-trip add-on charges + dropoff payment collection (02-trip-lifecycle.md).
  const [addOnsSheetOpen, setAddOnsSheetOpen] = useState(false);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  // At-completion collection (Mode C): the hosted-page link/QR for the currently
  // open PaymentCollectionSheet, and which TripNos have settled via trip:paymentSettled.
  const [collectionState, setCollectionState] = useState(EMPTY_COLLECTION);
  /**
   * Collection-time edits to the trip's charge rows: `{ [Trip_Price.Id]: amountCents }`.
   * These are NOT saved on their own — they ride in the `requestTripCollection` bundle
   * and AD applies them only on confirmed settlement, in the same SQL transaction as
   * the payment. Cleared whenever the sheet closes or the server rejects the bundle.
   */
  const [chargeEdits, setChargeEdits] = useState({});
  // Trip.Id (JOB info.jobID) → jobNumber bucket. Payment maps below are keyed by
  // tripKey(jobNumber) for legacy reasons; every server push is keyed by the GUID, so
  // this index is what makes a Paid push land on the trip the driver is looking at.
  const tripIdIndexRef = useRef({});
  // How each settled trip was paid (receipt paymentMethod), keyed like tripPayments.
  const [settlementInfo, setSettlementInfo] = useState({});
  // Metered trips: PRICE sent from the payment flow, waiting for PRICE|Ok.
  const [fareFinalize, setFareFinalize] = useState(IDLE_FINALIZE);
  // Manual fare entry for a metered trip with no running SoftMeter (fleet has the
  // meter disabled, or the trip carried no usable meter rates). Without this the
  // trip can never reach priceFinal:true and only cash can be collected.
  const [manualFare, setManualFare] = useState({ open: false, waypoint: null });
  const [cashState, setCashState] = useState({ pending: false, error: null });
  const [receiptState, setReceiptState] = useState(EMPTY_RECEIPT);
  // True while a manual "Check payment status" round-trip is in flight.
  const [checkingPayment, setCheckingPayment] = useState(false);
  // 'endTrip' (arrived, may complete from the Paid screen) | 'midTrip' (Loaded; QR only).
  const [paymentSheetSource, setPaymentSheetSource] = useState('endTrip');
  // Live settlements observed THIS session (trip:paymentSettled). Deliberately not the
  // only source of paid truth any more — see tripPayments below.
  const [paidTripKeys, setPaidTripKeys] = useState(() => new Set());
  // Server-authoritative payment block per trip, keyed by tripKey(jobNumber), captured
  // at ingestion from the job payload. paidTripKeys alone was insufficient: it starts
  // empty on every mount and trip:paymentSettled does not re-fire for an
  // already-settled trip, so a paid trip read as unpaid after any reload/remount.
  // Kept separate from tripPricingRef because that write is gated on pricing rows
  // existing, and a trip can have authoritative payment state with no pricing rows.
  const [tripPayments, setTripPayments] = useState({});
  // Trips whose settlement the server could not reconcile — must not read as "Paid".
  const [reconcileTripKeys, setReconcileTripKeys] = useState(() => new Set());

  // Server payment block for the displayed trip, keyed by TripNo with a Trip.Id
  // fallback (a Drivers-room push arrives under the GUID). Both the payment sheet's
  // `payment` prop and its fallbackFare guard read it, so it is derived once here.
  // Must stay BELOW the tripPayments declaration above — it is a plain const, not a
  // hook, so hoisting it up next to firstWp reads the binding before initialization.
  const firstPaymentBlock = firstWp
    ? tripPayments[firstTripKey] || tripPayments[tripIdKey(firstWp.tripId)] || null
    : null;

  /**
   * Server-authoritative payment status for a waypoint's trip. Accepts the jobNumber
   * bucket plus the Trip.Id so a settlement recorded under either key is honoured.
   */
  const paymentStatusForTrip = useCallback((jobNumber, tripId) => {
    const k = tripKey(jobNumber);
    const gk = tripIdKey(tripId);
    return resolveTripPaymentStatus(tripPayments[k] || (gk ? tripPayments[gk] : undefined), {
      settledLive: paidTripKeys.has(k) || paidTripKeys.has(String(jobNumber)) || (!!gk && paidTripKeys.has(gk)),
      needsReconciliation: reconcileTripKeys.has(k) || (!!gk && reconcileTripKeys.has(gk)),
    });
  }, [tripPayments, paidTripKeys, reconcileTripKeys]);

  /** Bucket for a server Trip.Id push: the displayed trip's jobNumber when known. */
  const paymentKeyForTripId = (tripId, fallbackKey) => {
    const gk = tripIdKey(tripId);
    return tripIdIndexRef.current[gk] || fallbackKey || gk;
  };

  /**
   * Record a settlement for one trip (trip:paymentSettled, tripCollectionResult
   * success, or an EPMTM cash choice). needsReconciliation never reads as Paid.
   */
  /**
   * Confirm a settlement with the server before showing "Payment complete".
   *
   * A hosted card page can be opened and abandoned: the driver taps through, HBSS never
   * captures, and nothing was actually collected. Treating any settlement-shaped signal
   * as proof of payment showed a green "Payment complete" for a trip whose money never
   * arrived — the worst possible direction for this error, because the driver stops
   * trying to collect.
   *
   * So verify first. getTripPaymentState is the payment authority (it also expires stale
   * durable collections); only its paid/account-billed answer promotes the trip to Paid.
   * Anything else leaves the collection screen up with an actionable message, so the
   * driver can retry the card or take cash.
   */
  const confirmSettlement = useCallback(async (tripId, info = {}, fallbackKey) => {
    if (!tripId) {
      // No Trip.Id to verify against (a local cash choice). Those are recorded by AD
      // through their own EPMTM path, so trust the caller as before.
      applySettlementUnverified(tripId, info, fallbackKey);
      return;
    }
    const state = await comms.getTripPaymentStateSocket({ tripId });
    const payment = state?.payment;
    const serverPaid = state?.success === true && !!payment &&
      (payment.paid === true || payment.accountBilled === true ||
        String(payment.paymentStatus || '').trim().toLowerCase() === 'settled');
    if (serverPaid) {
      applySettlementUnverified(tripId, info, fallbackKey);
      return;
    }
    // Not settled. Say so, and keep the collection route open.
    const stillPending = payment?.paymentPending === true;
    setCollectionState({
      tripId: tripId || null,
      url: null,
      error: state?.success === false
        ? (state?.error || 'Could not confirm the payment with dispatch. Try again, or collect cash.')
        : stillPending
          ? 'Payment is still processing — wait a moment and check again, or collect cash.'
          : 'Payment was not completed. Try the card again, or collect cash.',
    });
  }, []);

  const applySettlementUnverified = (tripId, info = {}, fallbackKey) => {
    if (!tripId && !fallbackKey) return;
    const k = paymentKeyForTripId(tripId, fallbackKey);
    const gk = tripIdKey(tripId);
    if (info.needsReconciliation) {
      setReconcileTripKeys((prev) => { const next = new Set(prev); next.add(k); if (gk) next.add(gk); return next; });
      return;
    }
    setPaidTripKeys((prev) => { const next = new Set(prev); next.add(k); if (gk) next.add(gk); return next; });
    setTripPayments((prev) => ({
      ...prev,
      [k]: mergePaymentOverride(prev[k], {
        paid: true,
        paymentAmount: info.paymentAmount,
        paymentMethod: info.paymentMethod || prev[k]?.paymentMethod || 'Credit Card',
      }),
    }));
    setSettlementInfo((prev) => ({
      ...prev,
      [k]: {
        paymentMethod: info.paymentMethod || prev[k]?.paymentMethod || 'Credit Card',
        transactionId: info.transactionId || null,
        settledAt: info.settledAt || new Date().toISOString(),
      },
    }));
  };
  const [pendingAddOns, setPendingAddOns] = useState({}); // keyed by tripKey: { extraQty, toll, surcharge }

  const currentPendingAddOns = firstTripKey
    ? pendingAddOns[firstTripKey] || { extraQty: {}, toll: 0, surcharge: 0 }
    : { extraQty: {}, toll: 0, surcharge: 0 };

  const handleChangeExtraQty = (id, qty) => {
    if (!firstTripKey) return;
    setPendingAddOns((prev) => {
      const cur = prev[firstTripKey] || { extraQty: {}, toll: 0, surcharge: 0 };
      return { ...prev, [firstTripKey]: { ...cur, extraQty: { ...cur.extraQty, [id]: qty } } };
    });
  };
  const handleChangeToll = (val) => {
    if (!firstTripKey) return;
    setPendingAddOns((prev) => {
      const cur = prev[firstTripKey] || { extraQty: {}, toll: 0, surcharge: 0 };
      return { ...prev, [firstTripKey]: { ...cur, toll: val } };
    });
  };
  const handleChangeSurcharge = (val) => {
    if (!firstTripKey) return;
    setPendingAddOns((prev) => {
      const cur = prev[firstTripKey] || { extraQty: {}, toll: 0, surcharge: 0 };
      return { ...prev, [firstTripKey]: { ...cur, surcharge: val } };
    });
  };
  const handleSendStopRequest = (address) => {
    if (!firstWp) return;
    comms.sendMessage(`Add Stop request: ${address} (Trip #${firstWp.jobNumber})`);
  };
  const resolvedFleetCode =
    fleetSettings?.unitId || fleetSettings?.fleetCode || localStorage.getItem('fleetCode') || '';

  /**
   * Refresh the authoritative payment summary for a trip (GET /API/Payment/active-trips)
   * and fold it into tripPayments: this is where baseFare / tolls / cardSurcharge /
   * cardAmount come from after PRICE|Ok. A settlement seen live in this session always
   * wins, and a snapshot from the brief post-PRICE cache window (fare still 0 while our
   * confirmed total is positive) only contributes its status flags.
   */
  const refreshTripPaymentState = async (wp) => {
    if (!wp?.tripId) return;
    const summary = await comms.getTripPaymentState({ tripId: wp.tripId });
    if (!summary) return;
    const k = tripKey(wp.jobNumber);
    setTripPayments((prev) => {
      const existing = prev[k] || {};
      const fetchedFare = Number(summary.fare);
      const stale = !(fetchedFare > 0) && Number(existing.paymentAmount) > 0;
      const merged = stale
        ? {
            ...existing,
            paid: summary.paid === true || existing.paid === true,
            paymentStatus: summary.paymentStatus || existing.paymentStatus,
            paymentPending: summary.paymentPending === true,
            accountBilled: summary.accountBilled === true,
            priceFinal: existing.priceFinal === true || summary.priceFinal === true,
          }
        : {
            ...existing,
            ...summary,
            paymentAmount: fetchedFare > 0 ? fetchedFare : existing.paymentAmount,
          };
      if (existing.paid) {
        return { ...prev, [k]: mergePaymentOverride(merged, { paid: true, paymentAmount: existing.paymentAmount, paymentMethod: existing.paymentMethod }) };
      }
      return { ...prev, [k]: merged };
    });
  };

  /**
   * Metered trip: the driver confirmed the fare. Send PRICE and wait for PRICE|Ok before
   * any collection is offered (never falls through to COMPM). On success the meter is
   * finalized and the local block is marked priceFinal with the confirmed total until
   * the authoritative refresh lands.
   */
  /**
   * Driver typed the fare because no meter was running. Build the same single meter
   * price row the meter path builds and reuse startFareFinalize, so the PRICE →
   * PRICE|Ok → collect sequence and all of its error handling are unchanged.
   *
   * The row needs the trip's meter rate-item id. When the server sent pricing rows we
   * use that id; otherwise we send an empty id, which is what the meter path already
   * does when `pricingData.meter.id` is absent.
   */
  const handleManualFareConfirm = (total) => {
    const wp = manualFare.waypoint;
    if (!wp) return;
    const tripNumber = wp.jobNumber;
    const pricingData = tripPricingRef.current[tripKey(tripNumber)];
    const priceItems = [{
      id: pricingData?.meter?.id || '',
      rate: total,
      name: 'Meter',
      type: 'meter',
    }];
    setManualFare({ open: false, waypoint: null });
    startFareFinalize(tripNumber, priceItems, total);
  };

  const startFareFinalize = (tripNumber, priceItems, total) => {
    const tk = tripKey(tripNumber);
    setFareFinalize({ tripNumber, status: 'pending', error: null, priceItems, total });
    setPaymentSheetOpen(true);
    comms.finalizePrice(tripNumber, priceItems)
      .then(() => {
        stopMeter(tripNumber);
        setTripPayments((prev) => {
          const existing = prev[tk] || {};
          const amount = Math.round((Number(total) || 0) * 100) / 100;
          // Drop any pre-PRICE server breakdown so the sheet derives rows from the new
          // total plus the server-tagged price rows until the refresh replaces them.
          const {
            baseFare, tolls, cardSurcharge, persistedTip, hasTolls, hasCardSurcharge,
            hasPersistedTip, cardAmount, walletAmount, ...rest
          } = existing;
          const paymentRequired = existing.accountBilled ? false : amount > 0;
          return {
            ...prev,
            [tk]: {
              ...rest,
              metered: true,
              priceFinal: true,
              paymentAmount: amount,
              fare: amount,
              paymentRequired,
              payable: paymentRequired && !existing.paid && !existing.paymentPending,
            },
          };
        });
        setFareFinalize((prev) => (tripKey(prev.tripNumber) === tk ? { ...prev, status: 'done', error: null } : prev));
        const wp = jobDetailsRef.current.find((j) => tripKey(j.jobNumber) === tk);
        if (wp) refreshTripPaymentState(wp);
      })
      .catch((err) => {
        setFareFinalize((prev) => (
          tripKey(prev.tripNumber) === tk
            ? { ...prev, status: 'error', error: err?.message || 'Fare confirmation failed. Please retry.' }
            : prev
        ));
      });
  };

  const retryFareFinalize = () => {
    if (fareFinalize.tripNumber && fareFinalize.priceItems) {
      startFareFinalize(fareFinalize.tripNumber, fareFinalize.priceItems, fareFinalize.total);
    }
  };

  /**
   * Open Collect Payment for a waypoint. source 'endTrip' = driver ended the trip at the
   * drop-off (may complete from the Paid screen); 'midTrip' = RiderApp QR while Loaded
   * (completion stays on the normal arrival step).
   *
   * A running meter means the fare is not final: confirm it first, PRICE → PRICE|Ok,
   * then the sheet opens with the authoritative payment state.
   */
  const openPaymentSheet = (wp, source = 'endTrip') => {
    const target = wp || firstWp;
    if (!target) return;
    const tk = tripKey(target.jobNumber);
    setPaymentSheetSource(source);
    setCollectionState(EMPTY_COLLECTION);
    setCashState({ pending: false, error: null });
    setReceiptState(EMPTY_RECEIPT);
    setFareFinalize(IDLE_FINALIZE);

    const runningJn = getRunningJobNumber();
    const meterOnThisTrip = meterRunning && !!runningJn && tripKey(runningJn) === tk;
    if (meterOnThisTrip && source === 'endTrip') {
      const fareData = getFareBreakdown();
      const stats = getMeterStats();
      pauseMeter(target.jobNumber);
      if (fareData) {
        setFareConfirmModal({ show: true, tripNumber: target.jobNumber, fareData, stats, purpose: 'payment' });
        return;
      }
      reEnableMeter(target.jobNumber);
    }
    // No meter on this trip, but the fare is still not final: the driver must supply
    // it, or QR/card can never unlock. This is the only route to PRICE for a fleet
    // running with the SoftMeter disabled.
    if (!meterOnThisTrip && source === 'endTrip' && needsManualFare(target)) {
      setManualFare({ open: true, waypoint: target });
      return;
    }
    setPaymentSheetOpen(true);
    refreshTripPaymentState(target);
  };

  const handlePaymentSheetClose = () => {
    if (fareFinalize.status === 'pending') return;
    // A confirmed-but-unacknowledged fare: resume the paused meter exactly like Cancel
    // on the fare modal, so the trip is left as it was before End Trip.
    if (fareFinalize.status === 'error' && fareFinalize.tripNumber) reEnableMeter(fareFinalize.tripNumber);
    setFareFinalize(IDLE_FINALIZE);
    setPaymentSheetOpen(false);
    setCollectionState(EMPTY_COLLECTION);
    // Charge edits are collection-scoped: unsent edits must not leak into the next
    // attempt, and sent ones are already the server's problem.
    setChargeEdits({});
  };

  // Cash: the legacy EPMTM message, then COMPM from the Paid screen (socket order
  // preserved). The server sends no EPMTM acknowledgement, so this is recorded locally
  // with method Cash so View/Text receipt can name it.
  /**
   * Re-ask AD whether this trip actually settled.
   *
   * Used after a card attempt that did not confirm. Two outcomes matter to the driver:
   * the money DID arrive (a webhook was simply still in flight, so the trip flips to
   * Paid and no second charge is created), or it did not (the message stands and the
   * cash/retry routes remain). Checking is offered BEFORE recreating a payment so an
   * already-paid rider is never charged twice.
   */
  const handleCheckPaymentStatus = async () => {
    if (!firstWp?.tripId || checkingPayment) return;
    setCheckingPayment(true);
    try {
      const state = await comms.getTripPaymentStateSocket({ tripId: firstWp.tripId });
      const payment = state?.payment;
      const paid = state?.success === true && !!payment &&
        (payment.paid === true || payment.accountBilled === true ||
          String(payment.paymentStatus || '').trim().toLowerCase() === 'settled');
      if (paid) {
        applySettlementUnverified(firstWp.tripId, {
          paymentMethod: 'Credit Card',
          paymentAmount: payment.paymentAmount,
        }, tripKey(firstWp.jobNumber));
        setCollectionState(EMPTY_COLLECTION);
        return;
      }
      if (payment) {
        // Keep the server's own payment block so the sheet's amounts stay authoritative.
        setTripPayments((prev) => ({ ...prev, [tripKey(firstWp.jobNumber)]: payment }));
      }
      setCollectionState({
        tripId: firstWp.tripId,
        url: null,
        error: payment?.paymentPending === true
          ? 'Still processing — the card page has not settled yet. Check again in a moment, or collect cash.'
          : 'Not paid yet. Try the card again, or collect cash.',
      });
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleCollectCash = () => {
    if (!firstWp) return;
    setCashState({ pending: false, error: null });
    comms.updatePayment(firstWp.jobNumber, 'CASH');
    // Cash needs no server confirmation: the driver physically has the money and AD
    // sends no EPMTM acknowledgement to verify against.
    applySettlementUnverified(firstWp.tripId, { paymentMethod: 'Cash' }, tripKey(firstWp.jobNumber));
  };

  // ── Assisted card collection: verify the passenger, then collect ─────────
  // AutoDispatch attaches a payment to a CONTACT, so the passenger is identified by
  // SMS code before a hosted page exists. The amounts the driver chose are captured
  // when verification starts and replayed once AD confirms the contact.
  const [verifiedTripId, setVerifiedTripId] = useState(null);
  const selectedFareRef = useRef(null);

  const handleSelectFare = (amounts) => { selectedFareRef.current = amounts; };

  // A confirmed contact is not a payment: request the page straight away (the
  // confirmation deliberately does not open one).
  const handlePassengerVerified = () => {
    if (!firstWp?.tripId) return;
    setVerifiedTripId(firstWp.tripId);
    const amounts = selectedFareRef.current;
    if (amounts) {
      handleRequestHostedPage(amounts);
      return;
    }
    // The sheet always reports the chosen fare before starting verification, so this
    // is unreachable in the normal flow. Surfacing it as a collection error is still
    // better than leaving the driver on a waiting screen that can never resolve.
    setCollectionState({
      tripId: firstWp.tripId,
      url: null,
      error: 'The fare for this payment was lost. Go back and choose the amount again.',
    });
  };

  // ALREADY_PAID / PAYMENT_PENDING and friends: leave the dialogue and re-read the
  // authoritative state — the rider may have paid while the driver was typing.
  const handleOtpPaymentStateChanged = () => {
    if (firstWp) refreshTripPaymentState(firstWp);
  };

  const otpDialogue = usePassengerOtpDialogue({
    tripId: firstWp?.tripId,
    otpCapability: firstWp ? (tripPayments[firstTripKey] || tripPayments[tripIdKey(firstWp.tripId)])?.otp : null,
    onVerified: handlePassengerVerified,
    onPaymentStateChanged: handleOtpPaymentStateChanged,
  });

  // Manual card: the fare/tip were chosen on the sheet; AutoDispatch creates one HBSS
  // hosted page for cardAmount + tip. Resolved by the trip GUID (Trip.Id), never TripNo.
  const handleRequestHostedPage = ({ fareAmount, tipAmount, totalAmount }) => {
    if (!firstWp) return;
    if (!firstWp.tripId) {
      setCollectionState({ tripId: null, url: null, error: 'This trip has no server trip id yet — reload the trip before requesting a card page.' });
      return;
    }
    setCollectionState({ tripId: firstWp.tripId, url: null, error: null });
    // One bundle: fare + changed price items + total tip. The items are absolute
    // calculated amounts against existing Trip_Price ids; unchanged rows are omitted
    // and keep their stored value. No separate PRICE call — AD writes the items, the
    // tip and the payment together, and only once the payment actually settles.
    const items = buildCollectionItems(
      editableChargeRows(firstTripPricing?.pricing),
      chargeEdits
    );
    const sent = comms.requestTripCollection({
      vehicleId: userSettings?.vehicleId,
      tripId: firstWp.tripId,
      fareAmount,
      tipAmount,
      totalAmount,
      items,
      presentation: 'embedded',
    });
    if (!sent) {
      setCollectionState({ tripId: firstWp.tripId, url: null, error: 'Not connected to dispatch. Reconnect and try again.' });
    }
  };

  const receiptMethodFor = (wp) => receiptPaymentMethod(
    settlementInfo[tripKey(wp.jobNumber)]?.paymentMethod || tripPayments[tripKey(wp.jobNumber)]?.paymentMethod
  );

  const handleViewReceipt = () => {
    if (!firstWp?.tripId) {
      setReceiptState({ ...EMPTY_RECEIPT, error: 'This trip has no server trip id yet.' });
      return;
    }
    setReceiptState({ ...EMPTY_RECEIPT, loading: true });
    if (!comms.getTripReceipt({ tripId: firstWp.tripId, paymentMethod: receiptMethodFor(firstWp) })) {
      setReceiptState({ ...EMPTY_RECEIPT, error: 'Not connected to dispatch. Reconnect and try again.' });
    }
  };

  const handleTextReceipt = (mobileNumber) => {
    if (!firstWp?.tripId) {
      setReceiptState({ ...EMPTY_RECEIPT, error: 'This trip has no server trip id yet.' });
      return;
    }
    setReceiptState({ ...EMPTY_RECEIPT, loading: true, sentTo: mobileNumber });
    if (!comms.sendTripReceiptSms({ tripId: firstWp.tripId, mobileNumber, paymentMethod: receiptMethodFor(firstWp) })) {
      setReceiptState({ ...EMPTY_RECEIPT, error: 'Not connected to dispatch. Reconnect and try again.' });
    }
  };

  // Paid (or no charge) and arrived: the normal Live completion — COMPM. PRICE has
  // already been acknowledged for a metered trip, so the meter is not running here.
  const handleCompleteFromSheet = () => {
    if (!firstWp) return;
    setPaymentSheetOpen(false);
    setFareFinalize(IDLE_FINALIZE);
    setCollectionState(EMPTY_COLLECTION);
    handleWaypointAction('complete', firstWp.jobNumber, firstWp.type);
  };

  /**
   * The fare this device already knows for a trip the server has not priced yet.
   * A flag trip is quoted at creation and that amount rides on the waypoint /
   * job payload, but Trip.TripCost stays a placeholder until the drop-off price
   * is sent — so without this the sheet would show the placeholder.
   * Returns undefined when nothing usable is known, so `??` chains fall through.
   */
  const quotedFareForTrip = (wp) => {
    if (!wp) return undefined;
    const pricing = tripPricingRef.current[tripKey(wp.jobNumber)];
    const candidates = [
      wp.fare, wp.quotedFare, wp.price, wp.tripCost,
      pricing?.disposition?.price?.base,
      pricing?.disposition?.Price?.base,
    ];
    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return undefined;
  };

  /**
   * True when the driver has to type the fare because nothing else can produce one.
   *
   * The server holds priceFinal:false on a metered trip until it receives PRICE, and
   * the SoftMeter's confirmation modal is normally what sends it. With no meter — the
   * fleet disabled it, or the trip carried no usable rates — that path does not exist,
   * so without manual entry the trip is stuck at "fare not final" and only cash works.
   *
   * Deliberately NOT gated on `metered`: a flag trip on a meter-disabled fleet reports
   * fareNotFinal too, and it needs the same escape. Trips that are already priced, paid,
   * account-billed or no-charge never reach here because the state check excludes them.
   */
  const needsManualFare = (wp) => {
    if (!wp) return false;
    const status = paymentStatusForTrip(wp.jobNumber, wp.tripId);
    return status?.state === TRIP_PAYMENT_STATE.FARE_NOT_FINAL;
  };

  const collectionForTrip =
    !collectionState.tripId || (!!firstWp?.tripId && tripIdKey(collectionState.tripId) === tripIdKey(firstWp.tripId));

  // For desktop/tablet, we show two columns; for phone, the existing stacked layout.
  const containerClass = isPhone
    ? 'flex flex-col' // phone: stacked layout
    : 'flex flex-row h-full'; // desktop: side-by-side layout filling height

  const mapSectionStyle = isPhone
    ? { padding: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }
    : { padding: '5px', flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const itinerarySectionStyle = isPhone
    ? { width: '100%' }
    : { flex: '1', overflowY: 'auto' };

  return (
      <div className="bg-slate-100" style={{height: `var(--app-content-height)`}}>
        <GeolocationHandler
          onLocationUpdate={handleLocationUpdate}
          setIsAccurate={setIsAccurate}
          onRawPosition={meterLocationUpdate}
          meterActive={meterRunning}
        />

        {autoArriveBanner && (
          <div
            className="fixed top-2 left-1/2 -translate-x-1/2 z-[1200] rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 shadow-lg"
            role="status"
          >
            📍 Arrived at pickup — status updated automatically
          </div>
        )}

        {/* Vehicle Status Bar - moved to bottom with trip integration */}
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-2 z-50">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-sm">
                Status: {currentTrip ? currentTrip.status : vehicleStatus}
                {currentTrip && <span className="ml-2">Trip {currentTrip.tripNumber}</span>}
              </span>
              {driverDetails && (
                <span className="text-xs text-blue-200">
                  {driverDetails.number}: {driverDetails.name}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {/* Trip status specific buttons */}
              {currentTrip ? (
                <>
                 
                </>
              ) : (
                /*
                 * Vehicle status buttons (no trip in progress).
                 *
                 * Clear is offered for any on-shift status, not just 'Busy'. The old
                 * `vehicleStatus === 'Busy'` test meant a driver left in a stale
                 * trip-lifecycle status (Dispatched/Loaded with no trip on screen) had
                 * no button at all and no way to make themselves available again.
                 * OffService is excluded: that driver is logged off, and going
                 * available is what the shift/logon flow is for. AutoDispatch
                 * validates every request regardless of what we show.
                 */
                <>
                  {vehicleStatus === 'OffService' ? null : vehicleStatus === 'Clear' ? (
                    <button
                      onClick={() => handleVehicleStatusChange('Busy')}
                      disabled={pendingVehicleStatus === 'Busy'}
                      className="px-2 py-1 bg-red-500 text-xs rounded disabled:opacity-60"
                    >
                      {pendingVehicleStatus === 'Busy' ? 'Sending...' : 'Busy'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleVehicleStatusChange('Clear')}
                      disabled={pendingVehicleStatus === 'Clear'}
                      className="px-2 py-1 bg-green-500 text-xs rounded disabled:opacity-60"
                    >
                      {pendingVehicleStatus === 'Clear' ? 'Clearing...' : 'Clear'}
                    </button>
                  )}
                </>
              )}
              
              {/* Messages Button with Flashing Animation */}
              <button 
                onClick={handleShowMessages}
                className={`px-2 py-1 text-xs rounded transition-colors duration-300 ${
                  isFlashing 
                    ? 'bg-orange-500 text-white' 
                    : hasUnreadMessages 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-blue-500 text-white'
                }`}
              >
                Messages{hasUnreadMessages && !showMessages && ' (!)'
                }
              </button>
            </div>
          </div>
        </div>

        {/* Messages Modal */}
        <Messages
          isOpen={showMessages}
          onClose={handleCloseMessages}
          // Was driverDetails?.number — that's the DRIVER's number, not the vehicle's
          // UnitID, and it's only populated once dispatch sends driver details. The
          // server keys chat on the vehicle unit, so resolve it the same way the socket
          // room join does; fall back to the driver number only if nothing else resolves.
          vehicleNumber={resolveVehicleUnitId({ userSettings }) || driverDetails?.number}
          driverName={driverDetails?.name}
          messages={messages}
          onSendMessage={handleSendMessage}
          onMarkAsRead={handleMarkAsRead}
        />

        {center && (
          <div className={containerClass} style={{ marginTop: '0px', paddingBottom: '60px' }}>
            
            
            {/* Map section */}
            <div ref={mapContainerRef} style={mapSectionStyle}>
              <WaypointMap
                isPhone={isPhone}
                googleApiKey={googleApiKey}
                googleMapsLoaded={googleMapsLoaded}
                center={center}
                waypoint={jobDetails[0]}
                allWaypoints={jobDetails}
                vehicleOrientation={vehicleOrientation}
                vehicleStatus={vehicleStatus}
                expectedTime={currentEta}
                routeDriveSummary={routeDriveSummary}
                onDirectionsCalculated={handleDirectionsCalculated}
                isAccurate={isAccurate}
                handleRequestDirections={handleRequestDirections}
                onWaypointAction={handleWaypointAction}
                softMeterState={softMeterState}
                softMeterRouteOverride={softMeterRouteOverride}
                onSetSoftMeterRouteOverride={handleSetSoftMeterRouteOverride}
                onClearSoftMeterRouteOverride={handleClearSoftMeterRouteOverride}
                meterRatesReady={meterRatesReady}
                canFlag={canFlag}
                fareConfirmationOpen={
                  !!(fareConfirmModal.show && fareConfirmModal.fareData)
                }
                onFlagTripModalClosed={handleFlagTripModalClosed}
                onFlagTripModalOpen={onFlagTripModalOpen}
                onFlagTripModalDismiss={onFlagTripModalDismiss}
                registerPendingFlagAck={registerPendingFlagAck}
                fleetSettings={fleetSettings}
                onOpenAddOns={() => setAddOnsSheetOpen(true)}
                onOpenPayment={openPaymentSheet}
                tripPaid={firstWp ? paymentStatusForTrip(firstWp.jobNumber, firstWp.tripId).isPaid : false}
                tripPaymentStatus={firstWp ? paymentStatusForTrip(firstWp.jobNumber, firstWp.tripId) : null}
                // Live status for THIS waypoint's trip. waypoint.status is only
                // stamped at JOB ingestion, so it is stale once the trip advances.
                tripStatus={
                  firstWp && currentTrip && tripKey(currentTrip.tripNumber) === tripKey(firstWp.jobNumber)
                    ? currentTrip.status
                    : firstWp?.status
                }
                onBrowseMarketplace={navigateTo ? () => navigateTo('marketplace') : undefined}
              />
            </div>

            {/* Itinerary section */}
            <div id="itinerary" className="flex flex-col" style={itinerarySectionStyle}>
              {jobDetails.length > 1 && (
                <Itinerary
                  isPhone={isPhone}      
                  jobs={jobDetails.slice(1).filter((job) => !job.completed)}
                  onTripSelect={displayTripInfo}
                />
              )}
            </div>
          </div>
        )}

        {/* Job Offer Modal */}
        {/* A completion AD refused. Without this the driver saw an empty trip list and
            assumed the trip was done, while it stayed ArrivedDestination on the server. */}
        {tripActionError && (
          <div className="fixed inset-x-0 top-0 z-[2300] p-3">
            <div className="mx-auto max-w-md rounded-xl bg-red-600 p-3 text-white shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-bold">
                    Trip not completed{tripActionError.tripNumber ? ` — #${tripActionError.tripNumber}` : ''}
                  </div>
                  <div className="mt-0.5 text-xs text-red-50">{tripActionError.message}</div>
                  {tripActionError.code === 'PRICE_REQUIRED' && (
                    <div className="mt-1 text-xs text-red-50">
                      Finalize the fare, then complete again.
                    </div>
                  )}
                  {(tripActionError.code === 'PAYMENT_REQUIRED' || tripActionError.code === 'PAYMENT_PENDING') && (
                    <div className="mt-1 text-xs text-red-50">
                      Collect payment, then complete again.
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setTripActionError(null)}
                  className="shrink-0 rounded-lg bg-red-700 px-2.5 py-1 text-xs font-semibold"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {showJobOffer && pendingOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200] p-4">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
              {/* Countdown banner — turns red for the last 3 seconds. */}
              <div
                className={`px-5 py-3 text-white transition-colors ${
                  offerCountdown > 0 && offerCountdown <= 3 ? 'bg-red-600' : 'bg-blue-700'
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-base font-bold">New Trip Offer</span>
                  <span className="text-sm font-semibold">#{pendingOffer.tripNumber}</span>
                </div>
                {offerCountdown > 0 && (
                  <div className="mt-0.5 text-xs font-medium text-white/90">
                    Auto-accepting in {offerCountdown}s
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                {/* Pickup / dropoff. The JOBOM offer payload carries a single `location`
                    (pickup street) — the full pickup/dropoff pair only arrives later on
                    JOBM, so fall back to that field when `locations` is absent. */}
                {pendingOffer.locations && pendingOffer.locations[0] ? (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
                    <span className="flex-1 text-gray-800">
                      {pendingOffer.locations[0].landmark ||
                        `${pendingOffer.locations[0].street_num} ${pendingOffer.locations[0].street_name}`}
                    </span>
                  </div>
                ) : pendingOffer.location ? (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
                    <span className="flex-1 text-gray-800">{pendingOffer.location}</span>
                  </div>
                ) : null}

                {pendingOffer.locations && pendingOffer.locations[1] && (
                  <div className="mt-1.5 flex items-start gap-2 text-sm">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                    <span className="flex-1 text-gray-800">
                      {pendingOffer.locations[1].landmark ||
                        `${pendingOffer.locations[1].street_num} ${pendingOffer.locations[1].street_name}`}
                    </span>
                  </div>
                )}

                {pendingOffer.info?.name && (
                  <div className="mt-3 truncate text-sm font-medium text-gray-900">
                    {pendingOffer.info.name}
                    {pendingOffer.info.account && (
                      <span className="ml-1 font-normal text-gray-500">· {pendingOffer.info.account}</span>
                    )}
                  </div>
                )}

                {/* Distance to pickup and zone — the only trip metrics the offer packet
                    actually carries. */}
                {(pendingOffer.distance != null || pendingOffer.zone) && (
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                    {pendingOffer.distance != null && pendingOffer.distance !== '' && (
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700">
                        {pendingOffer.distance} away
                      </span>
                    )}
                    {pendingOffer.zone && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">
                        Zone {pendingOffer.zone}
                      </span>
                    )}
                  </div>
                )}

                {pendingOffer.formattedTextTA && (
                  <div className="mt-3 rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
                    <div dangerouslySetInnerHTML={{ __html: pendingOffer.formattedTextTA }} />
                  </div>
                )}

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleAcceptTrip}
                    className="flex-1 rounded-lg bg-green-600 py-2.5 px-4 font-semibold text-white transition-transform active:scale-95 hover:bg-green-700"
                  >
                    Accept
                  </button>
                  <button
                    onClick={handleRejectTrip}
                    className="flex-1 rounded-lg bg-red-600 py-2.5 px-4 font-semibold text-white transition-transform active:scale-95 hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Input Modal */}
        {showMessageInput && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200]">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Send Message</h3>
              <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="w-full p-2 border rounded mb-4"
                rows="3"
                placeholder="Enter your message..."
              />
              <div className="flex gap-4">
                <button 
                  onClick={handleSendMessage}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Send
                </button>
                <button 
                  onClick={() => setShowMessageInput(false)}
                  className="flex-1 bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {paymentModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200]">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Payment Method</h3>
              <p className="mb-4">Select payment method for trip {paymentModal.tripNumber}:</p>
              <div className="flex flex-col gap-2">
                <button 
                  onClick={() => handlePaymentUpdate('CASH')}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
                >
                  Cash
                </button>
                <button 
                  onClick={() => handlePaymentUpdate('CREDIT')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Credit Card
                </button>
                <button 
                  onClick={() => handlePaymentUpdate('ACCOUNT')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700"
                >
                  Account
                </button>
                <button 
                  onClick={() => setPaymentModal({ show: false, tripNumber: null })}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fare modal only mounts when data exists so Total field initializes to calculated fare */}
        {fareConfirmModal.show && fareConfirmModal.fareData && (() => {
          const tk = fareConfirmModal.tripNumber
            ? tripKey(fareConfirmModal.tripNumber)
            : '';
          const pricingForTrip = tk ? tripPricingRef.current[tk] : null;
          const pendingForTrip = pendingAddOns[tk] || { extraQty: {}, toll: 0, surcharge: 0 };
          // Mid-trip AddOnChargesSheet selections (extras qty, toll, fees) are merged
          // in here as defaults rather than sent immediately, so they don't race the
          // PRICE-before-COMPM ack sequence used at trip completion.
          const eachExtras = Array.isArray(pricingForTrip?.pricing)
            ? pricingForTrip.pricing
                .filter((p) => String(p?.type || p?.Type || '').toLowerCase() === 'each')
                .map((p, i) => {
                  const k = String(p.id || `idx_${i}`);
                  const pendingQty = pendingForTrip.extraQty[k];
                  return pendingQty > 0 ? { ...p, qty: pendingQty } : p;
                })
            : [];
          const basePrice = pricingForTrip?.disposition?.price
            ?? pricingForTrip?.disposition?.Price
            ?? null;
          const dispPrice = (pendingForTrip.toll > 0 || pendingForTrip.surcharge > 0)
            ? {
                ...(basePrice || {}),
                toll: (Number(basePrice?.toll) || 0) + (pendingForTrip.toll || 0),
                surcharge: (Number(basePrice?.surcharge) || 0) + (pendingForTrip.surcharge || 0),
              }
            : basePrice;
          return (
            <FareConfirmationModal
              isOpen={fareConfirmModal.show}
              fareData={fareConfirmModal.fareData}
              extras={eachExtras}
              dispositionPrice={dispPrice}
              onConfirm={handleFareConfirm}
              onCancel={handleFareCancel}
              confirmLabel={fareConfirmModal.purpose === 'payment' ? 'Confirm & Collect Payment' : undefined}
            />
          );
        })()}

        {/* Metered/unpriced trip with no running meter: the driver supplies the fare,
            and it goes through the same PRICE → PRICE|Ok → collect path. */}
        <ItineraryFinalFareModal
          open={manualFare.open}
          waypoint={manualFare.waypoint ? {
            ...manualFare.waypoint,
            trip: {
              reservation: {
                number: manualFare.waypoint.jobNumber,
                price: quotedFareForTrip(manualFare.waypoint),
                meter: tripPricingRef.current[tripKey(manualFare.waypoint.jobNumber)]?.meter || null,
                wait: tripPricingRef.current[tripKey(manualFare.waypoint.jobNumber)]?.wait || null,
              },
            },
          } : null}
          pending={fareFinalize.status === 'pending'}
          error={fareFinalize.status === 'error' ? fareFinalize.error : null}
          onConfirm={handleManualFareConfirm}
          onCancel={() => setManualFare({ open: false, waypoint: null })}
        />

        <AddOnChargesSheet
          open={addOnsSheetOpen}
          onClose={() => setAddOnsSheetOpen(false)}
          waypoint={firstWp}
          extras={
            Array.isArray(firstTripPricing?.pricing)
              ? firstTripPricing.pricing.filter(
                  (p) => String(p?.type || p?.Type || '').toLowerCase() === 'each'
                )
              : []
          }
          pendingExtraQty={currentPendingAddOns.extraQty}
          onChangeExtraQty={handleChangeExtraQty}
          pendingToll={currentPendingAddOns.toll}
          pendingSurcharge={currentPendingAddOns.surcharge}
          onChangeToll={handleChangeToll}
          onChangeSurcharge={handleChangeSurcharge}
          softMeterState={softMeterState}
          onSendStopRequest={handleSendStopRequest}
        />

        <PaymentCollectionSheet
          open={paymentSheetOpen}
          onClose={handlePaymentSheetClose}
          // Trip GUID (waypoint.tripId, from JOB info.jobID) — the only id the RiderApp
          // QR, requestTripCollection, receipts and trip:paymentSettled match on. The
          // short jobNumber is display-only here.
          tripId={firstWp?.tripId}
          tripNumber={firstWp?.jobNumber}
          distanceText={
            softMeterState?.distance != null
              ? `${Number(softMeterState.distance).toFixed(1)} mi`
              : null
          }
          fleetCode={resolvedFleetCode}
          riderAppUrl={fleetSettings?.riderAppUrl}
          payment={firstPaymentBlock}
          priceItems={firstTripPricing?.pricing}
          // Collection-time charge editing. The edits are held here (not in the sheet)
          // so the request builder and the rejection handler share one source of truth.
          chargeEdits={chargeEdits}
          onChangeChargeEdit={(id, amountCents) => {
            setChargeEdits((prev) => {
              if (amountCents === null) {
                // Reverted to the server's amount — stop sending this row.
                const { [id]: _removed, ...rest } = prev;
                return rest;
              }
              return { ...prev, [id]: amountCents };
            });
          }}
          // The best locally-known fare, used ONLY when the server has not priced the
          // trip yet (its breakdown comes from Trip.TripCost, a placeholder until
          // then). In order: a running soft meter, a confirmed metered total, then
          // the trip's own quoted/booked price — which is what a flag trip has.
          //
          // A LIVE soft-meter reading is deliberately excluded once the server has
          // finalized the price. The meter keeps ticking while the payment sheet is
          // open, so passing it here made the sheet show a running meter figure as
          // the fare due — disagreeing with the RiderApp QR page (which reads the
          // server summary) and, worse, being sent as `fareAmount` to
          // requestTripCollection, where the server's final-price guard rejected it
          // with INVALID_AMOUNT ("Amount does not match the finalized trip price")
          // so the secure card page never opened.
          fallbackFare={
            (firstPaymentBlock?.priceFinal === true ? undefined : softMeterState?.fare)
            ?? fareFinalize.total
            ?? quotedFareForTrip(firstWp)
          }
          paymentStatus={firstWp ? paymentStatusForTrip(firstWp.jobNumber, firstWp.tripId) : null}
          fareFinalizing={fareFinalize.status === 'pending'}
          fareFinalizeError={fareFinalize.status === 'error' ? fareFinalize.error : null}
          onRetryFinalize={retryFareFinalize}
          onEnterFareManually={
            firstWp && needsManualFare(firstWp)
              ? () => { setPaymentSheetOpen(false); setManualFare({ open: true, waypoint: firstWp }); }
              : null
          }
          onCollectCash={handleCollectCash}
          cashPending={cashState.pending}
          cashError={cashState.error}
          onRequestHostedPage={handleRequestHostedPage}
          otpDialogue={otpDialogue}
          onSelectFare={handleSelectFare}
          passengerVerified={!!firstWp?.tripId && verifiedTripId === firstWp.tripId}
          collectionUrl={collectionForTrip ? collectionState.url : null}
          collectionError={collectionForTrip ? collectionState.error : null}
          onCheckPaymentStatus={firstWp?.tripId ? handleCheckPaymentStatus : undefined}
          checkingPaymentStatus={checkingPayment}
          onViewReceipt={handleViewReceipt}
          onTextReceipt={handleTextReceipt}
          receipt={receiptState}
          // Header/footer identity for a receipt the driver saves locally. The fleet
          // code stands in when no display name is configured, so the document is
          // never issued anonymously.
          fleetName={fleetSettings?.fleetName || fleetSettings?.siteName || resolvedFleetCode}
          driverName={userSettings?.Name || userSettings?.name || ''}
          onCompleteTrip={handleCompleteFromSheet}
          canCompleteNow={paymentSheetSource === 'endTrip'}
        />

        <WaypointModal
          isOpen={isModalOpen}
          onClose={closeModal}
          waypoint={selectedTrip && selectedTrip[0]}
          allWaypoints={jobDetails}
          vehicleLocation={center}
          waypointIndex={selectedTrip && selectedTrip[0] ? 
            jobDetails.findIndex(wp => 
              wp.jobNumber === selectedTrip[0].jobNumber && 
              wp.type === selectedTrip[0].type
            ) : undefined
          }
          fleetSettings={fleetSettings}
        />

        {!isAccurate && (
          <GpsWarningTag onLocationUpdate={handleLocationUpdate} setIsAccurate={setIsAccurate} />
        )}

        {/* Schedule Update Toast */}
        {showScheduleToast && (
          <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-orange-500 text-white rounded-lg shadow-lg w-full max-w-md flex items-center justify-between p-4"
              style={{ height: '120px' }}
            >
              <div className="flex-1 flex items-center justify-center">
                <p className="text-lg font-semibold">Your schedule has been updated</p>
              </div>
              <button
                onClick={handleDismissScheduleToast}
                className="ml-4 bg-white text-orange-500 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors text-lg"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
  );
}

export default LiveDispatch;
